import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Plus, X, Calendar, Users, ArrowUp, ArrowDown, GripVertical, Lock, AlertTriangle, Send, Undo2, ChevronDown, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Services
import surveyService from '../../services/surveyService';
import studyBuilderService from '../../services/studyBuilderService';
import reminderService from '../../services/reminderService';

// Custom components
import { MultiSelectDropdown, SingleSelectDropdown } from '../Shared/CustomDropdowns';
import Tooltip from '../Shared/Tooltip';
import SchedulePreviewModal from './SchedulePreviewModal';
import AmendScheduleModal from './AmendScheduleModal';
import ScheduleVersionHistoryPanel from './ScheduleVersionHistoryPanel';
import SubmitForApprovalModal from './SubmitForApprovalModal';
import { withdrawStudyDraft, getStudyDraftReviewContext } from '../../services/reviewWorkflowApi';

// A live (published) schedule carries a backend UUID; draft-only schedules do
// not. The amend / version-history actions only apply to live schedules.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isLiveScheduleId = (id) => typeof id === 'string' && UUID_RE.test(id);

// Defensive select component to prevent null values
const SafeSelect = ({ value, ...props }) => {
  // Ensure value is never null
  const safeValue = value === null || value === undefined ? '' : value;
  return <select value={safeValue} {...props} />;
};

// Defensive input component for numeric values
const SafeInput = ({ value, type = "text", ...props }) => {
  // For number inputs, ensure 0 is properly preserved
  const safeValue = value === null || value === undefined ? '' : value;
  return <input type={type} value={safeValue} {...props} />;
};

// FECOA-1660: an on-site (clinic visit) schedule paired with multiple intra-day
// timepoints is logically inconsistent with how clinic visits work. This is an
// informational warning only — it never blocks save.
export const isOnsiteTimepointConflict = (schedule) =>
  schedule?.siteType === 'on-site' && (schedule?.timepoints || []).length > 0;

// Amber, non-blocking warning banner used for the on-site + timepoints conflict.
const OnsiteTimepointWarning = ({ text, className = '' }) => (
  <div
    role="alert"
    className={`flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 text-sm text-amber-800 dark:text-amber-300 ${className}`}
  >
    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
    <span>{text}</span>
  </div>
);

// Constants
const MINUTES_OPTIONS = Array.from({ length: 8 }, (_, i) => (i + 1) * 15); // 15 to 120 minutes in 15-min increments

// Study Start Day helpers — translate an offset into plain English + a projected date,
// and compute the offset between two picked dates for the inline calculator.
const parseIsoDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const daysBetweenIso = (fromIso, toIso) => {
  const from = parseIsoDate(fromIso);
  const to = parseIsoDate(toIso);
  if (!from || !to) return 0;
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
};

const describeStudyStartDay = (rawValue, refEnrollmentIso) => {
  if (rawValue === '' || rawValue === '-' || rawValue === null || rawValue === undefined) return null;
  const n = parseInt(rawValue, 10);
  if (Number.isNaN(n)) return null;
  const ref = parseIsoDate(refEnrollmentIso);
  if (!ref) return null;

  const target = new Date(ref);
  target.setDate(target.getDate() + n);
  const fullDateFmt = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
  const targetFmt = target.toLocaleDateString('en-US', fullDateFmt);
  const refFmt = ref.toLocaleDateString('en-US', fullDateFmt);

  let relative;
  if (n === 0) relative = 'Same day as enrollment.';
  else if (n === 1) relative = '1 day after enrollment.';
  else if (n === -1) relative = '1 day before enrollment (pre-study).';
  else if (n > 0) relative = `${n} days after enrollment.`;
  else relative = `${Math.abs(n)} days before enrollment (pre-study).`;

  return {
    relative,
    projection: `For a participant enrolling ${refFmt}, study starts ${targetFmt}.`,
  };
};

const StudyDesignsPage = () => {
  const navigate = useNavigate();
  const { studyName } = useParams();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showValidation, setShowValidation] = useState(false);
  // Locked while the draft is in the approval workflow (in_review / approved):
  // no one may edit the design until it is recalled, approved, or denied.
  const [reviewLocked, setReviewLocked] = useState(false);
  // Approval workflow (FECOA-1600/1601/1602): draft identity + submit/withdraw UI.
  const [draftId, setDraftId] = useState(null);
  const [denialFeedback, setDenialFeedback] = useState('');
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  // Arms
  const [arms, setArms] = useState(['Arm A']);

  // Study Start Day - separate state that isn't dependent on schedules
  const [studyStartDay, setStudyStartDay] = useState('');

  // Inline date calculator for Study Start Day (computes offset from two picked dates)
  const todayIsoForCalc = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const [showStartDayCalc, setShowStartDayCalc] = useState(false);
  const [calcRefEnrollment, setCalcRefEnrollment] = useState(todayIsoForCalc);
  const [calcTargetStart, setCalcTargetStart] = useState(todayIsoForCalc);

  // Schedules
  const [schedules, setSchedules] = useState([]);
  const [selectedScheduleIndex, setSelectedScheduleIndex] = useState(null);
  const [showAmendModal, setShowAmendModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // We fetch these from the backend
  const [surveys, setSurveys] = useState([]);        // standard surveys
  // True until the survey/reminder name lists (plus per-id fallbacks) are in.
  // Selection chips render a spinner instead of a bare id while these hold.
  const [surveysLoading, setSurveysLoading] = useState(true);
  const [remindersLoading, setRemindersLoading] = useState(true);
  // FECOA-1624: study-level caregiver support flag, surfaced via the survey-list
  // endpoint meta. Defaults true so the Caregiver option is never hidden when the
  // flag is unknown (avoids regressions on older API responses).
  const [allowCaregivers, setAllowCaregivers] = useState(true);
  const [missingForms, setMissingForms] = useState([]);
  const [emailReminders, setEmailReminders] = useState([]);
  const [pushReminders, setPushReminders] = useState([]); // text -> push

  // A blank assessment window. Times are stored in UTC (see TimeInput).
  const makeDefaultWindow = () => ({
    name: '',
    openWindowDay: 0,
    closeWindowDay: 0,
    startTime: { hours: '00', minutes: '00', period: 'UTC' },
    endTime: { hours: '12', minutes: '00', period: 'UTC' }
  });

  // Legacy drafts stored a single `assessmentWindow` object; current drafts use
  // an `assessmentWindows` array. Normalize either shape to an array.
  const normalizeAssessmentWindows = (schedule) => {
    if (Array.isArray(schedule?.assessmentWindows) && schedule.assessmentWindows.length > 0) {
      return schedule.assessmentWindows.map((w) => ({ name: '', ...w }));
    }
    if (schedule?.assessmentWindow && typeof schedule.assessmentWindow === 'object') {
      return [{ name: '', ...schedule.assessmentWindow }];
    }
    // FECOA-1236: the Delivery Window is always shown, so guarantee a single
    // day-window entry even for schedules saved before the consolidation.
    return [makeDefaultWindow()];
  };

  // The schedule we're currently editing (mirrored from the schedules array)
  const createNewSchedule = () => {
    const defaultTime = { hours: '00', minutes: '00', period: 'UTC' };  // Use UTC time format
      
    return {
      name: '',
      type: '',
      studyStartDay: studyStartDay, // Use the independent studyStartDay state
      arms: [],
      standardSurveys: [],
      respondentType: 'participant', // FECOA-1522: schedule-level respondent type
      enablePushReminders: false,
      pushReminders: [],
      pushReminderTimers: [{ ...defaultTime }],
      enableEmailReminders: false,
      emailReminders: [],
      emailReminderTimers: [{ ...defaultTime }],
      enableMissingForms: false,
      missingForms: [],
      siteType: '',
      // FECOA-1236: the Delivery Window (Open/Close Day range) is always present
      // now — no enable checkbox. Seed a single day-window for every schedule.
      hasAssessmentWindow: true,
      assessmentWindows: [makeDefaultWindow()],
      assessmentTimers: [],
      timepoints: [],
      stages: [
        {
          name: '',
          startDay: '',
          endDay: '',
          frequency: { value: '', unit: 'days' }
        }
      ]
    };
  };

  const [editingSchedule, setEditingSchedule] = useState(createNewSchedule());
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const scheduleTypes = [
    'Daily Assessment',
    'Patient Prompted (Daily)',
    'Patient Prompted - Timer (Daily)',
    'Scheduled',
    'Patient Prompted (Scheduled)',
    'Patient Prompted - Timer (Scheduled)'
  ];
  const frequencyUnits = ['days', 'weeks', 'months', 'years'];

  // Schedule types where daily cadence is implied and the stage editor collapses
  // to a single Start Day + Duration block (no frequency/unit, no multi-stage).
  const IMPLIED_DAILY_TYPES = ['Patient Prompted (Daily)', 'Daily Assessment'];
  const isImpliedDailyType = (t) => IMPLIED_DAILY_TYPES.includes(t);
  const isStagesDisabled = isImpliedDailyType(editingSchedule.type);

  // FECOA-1236: the Delivery Window's day range (Open/Close Day) applies to EVERY
  // schedule type — including Patient Prompted, where the patient self-starts
  // ad-hoc but only on the days the assessment is open. What Patient Prompted
  // types don't get is the intra-day clock-time config (see supportsTimepoints).
  const supportsAssessmentWindows = () => true;

  // Assessment timers only belong to the two "- Timer" types (FECOA-1625). The
  // timer drives the re-prompt cascade for acute-attack assessments; it must not
  // appear on Daily Assessment, Scheduled, or the non-timer Patient Prompted types.
  const isTimerType = (t) => !!t && t.includes('Patient Prompted - Timer');

  // Intra-day timepoints (the "Multiple times a day" delivery mode) apply to the
  // system-timed Scheduled and Daily Assessment types (FECOA-1236). The four
  // Patient Prompted variants are participant-/timer-triggered (ad-hoc), so fixed
  // clock-time slots don't apply and the multi-timepoint feature is hidden.
  const TIMEPOINT_TYPES = ['Scheduled', 'Daily Assessment'];
  const supportsTimepoints = (t) => TIMEPOINT_TYPES.includes(t);

  // FECOA-1236: "Once a day" vs "Multiple times a day" is inferred from the
  // timepoints, not a separate flag. Once-a-day is represented by a single
  // unnamed timepoint (the Name field is hidden for once-a-day); multiple-times
  // timepoints always carry a Name. An empty list also reads as once-a-day.
  const isOnceDeliveryMode = (schedule) => {
    const tps = schedule?.timepoints || [];
    if (tps.length === 0) return true;
    return tps.length === 1 && !(tps[0].name || '').trim();
  };

  // Mark required fields
  const RequiredFieldMarker = () => (
    <span className="text-red-500 ml-1" aria-label="required field">
      *
    </span>
  );

  // on mount, load existing data (arms, schedules, etc.)
  useEffect(() => {
    const loadDraftData = async () => {
      try {
        const drafts = await studyBuilderService.getStudyDrafts() || [];
        const currentDraft = drafts.find((d) => d.name === studyName);

        if (currentDraft) {
          localStorage.setItem('currentStudyDraftId', currentDraft.id.toString());
          setDraftId(currentDraft.id);
          // Published designs are locked too (FECOA-1674): editing requires a
          // Recall from the Study Drafts dashboard, which reverts to draft.
          setReviewLocked(['in_review', 'approved', 'published'].includes(currentDraft.review_status));
          // Surface the latest reviewer feedback so a resubmit can confirm it
          // was addressed (FECOA-1601).
          if (currentDraft.review_status === 'denied') {
            getStudyDraftReviewContext(currentDraft.id)
              .then((ctx) => {
                const last = ctx?.denial_history?.[0];
                if (last?.reason) setDenialFeedback(last.reason);
              })
              .catch(() => {});
          }
          if (currentDraft.data) {
            // arms
            if (
              Array.isArray(currentDraft.data.arms) &&
              currentDraft.data.arms.length > 0
            ) {
              setArms(currentDraft.data.arms);
            }

            // study start day (could be in different places depending on implementation)
            if (isValidStartDay(currentDraft.data.studyStartDay)) {
              setStudyStartDay(currentDraft.data.studyStartDay);
            } else if (
              Array.isArray(currentDraft.data.schedules) && 
              currentDraft.data.schedules.length > 0 && 
              currentDraft.data.schedules[0].studyStartDay !== undefined
            ) {
              setStudyStartDay(currentDraft.data.schedules[0].studyStartDay);
            }

            // schedules
            if (
              Array.isArray(currentDraft.data.schedules) &&
              currentDraft.data.schedules.length > 0
            ) {
              // Get current global arms to clean up schedule arms
              const currentGlobalArms = currentDraft.data.arms || [];
              
              // Clean up schedules and remove any arm references that don't exist in global arms
              const updatedSchedules = currentDraft.data.schedules.map(schedule => ({
                ...schedule,
                // Clean up arms array - only keep arms that exist in global arms
                arms: Array.isArray(schedule.arms) 
                  ? schedule.arms.filter(armName => currentGlobalArms.includes(armName))
                  : [],
                assessmentTimers: schedule.type && schedule.type.includes('Patient Prompted - Timer')
                  ? (schedule.assessmentTimers || [{
                      enabled: true,
                      minutes: 15,
                      addAlarm: false
                    }])
                  : undefined
              }));
              setSchedules(updatedSchedules);
              return updatedSchedules;
            }
          }
        }
        return [];
      } catch (e) {
        console.error('Error loading draft:', e);
        toast.error('Failed to load existing study data');
        return [];
      } finally {
        setLoading(false);
      }
    };

    // fetch standard + missing surveys
    const fetchAllSurveys = async (draftSchedulesPromise) => {
      try {
        // Try to get surveys specific to the study. Fail soft: the by-name
        // lookup errors whenever the draft name has drifted from the Study
        // title, and the per-id resolution below must still run so chips can
        // show survey names instead of bare ids.
        const respStandard = await studyBuilderService.getSurveysByStudyName(studyName).catch((e) => {
          console.warn('[ScheduleBuilder] Study-name survey lookup failed:', e?.message);
          return null;
        });
        if (respStandard) {
          const standardSurveys = (respStandard?.surveys || []).filter(s => s.survey_type === 'standard');
          setSurveys(standardSurveys);

          // FECOA-1624: capture study caregiver support flag (default true if absent)
          if (typeof respStandard?.meta?.allow_caregivers === 'boolean') {
            setAllowCaregivers(respStandard.meta.allow_caregivers);
          }
          
          // If we have linked surveys in a schedule, make sure they're in the correct order
          if (schedules && schedules.length > 0) {
            schedules.forEach(schedule => {
              if (schedule.standardSurveys && schedule.standardSurveys.length > 1) {
                const idsInSchedule = schedule.standardSurveys.map((id) => id.toString());
                const headSurvey = standardSurveys.find(
                  (s) =>
                    idsInSchedule.includes(s.id.toString()) &&
                    Array.isArray(s.linked_surveys) &&
                    s.linked_surveys.length > 0
                );

                if (headSurvey) {
                  const headId = headSurvey.id.toString();
                  const followerIds = headSurvey.linked_surveys.map((id) => id.toString());
                  const newOrder = [headId, ...followerIds.filter((id) => idsInSchedule.includes(id))];
                  const remaining = idsInSchedule.filter((id) => !newOrder.includes(id));
                  schedule.standardSurveys = [...newOrder, ...remaining];
                }
              }
            });
          }
        }
        
        // The unified endpoint returns the study's full published library
        // regardless of its `type` param, so the missing-forms list reuses the
        // response above instead of paying a second identical round trip.
        if (respStandard) {
          setMissingForms(respStandard?.surveys || []);
        }

        // The standard/missing lists are filtered by survey_type — and miss
        // entirely when the draft name has drifted from the Study title — so a
        // schedule can reference surveys with no loaded name and render the
        // bare ID as its chip label. Resolve those individually, against the
        // draft's actual schedules: the `schedules` state var is still the
        // stale pre-load value inside this effect's closure.
        const draftSchedules = (await draftSchedulesPromise) || [];
        const loadedSurveys = respStandard?.surveys || [];
        const resolved = await studyBuilderService.resolveMissingScheduleSurveys(draftSchedules, loadedSurveys);
        if (resolved.length > 0) {
          const missing = resolved.filter((s) => s.survey_type === 'missing_form');
          const rest = resolved.filter((s) => s.survey_type !== 'missing_form');
          if (rest.length > 0) setSurveys(prev => [...prev, ...rest]);
          if (missing.length > 0) setMissingForms(prev => [...prev, ...missing]);
        }
      } catch (err) {
        console.error('Error fetching surveys:', err);
        toast.error('Failed to load surveys');
      } finally {
        setSurveysLoading(false);
      }
    };

    // Fetch reminders using reminderService instead of studyBuilderService
    const fetchReminders = async () => {
      try {
        const draftId = localStorage.getItem('currentStudyDraftId');
        if (!draftId) {
          console.error('No draft ID found in localStorage');
          return;
        }

        const reminders = await reminderService.getReminderTemplates(draftId);
        
        // Ensure we have an array and filter out any invalid items
        const validReminders = Array.isArray(reminders.results) ? reminders.results : [];
        
        // Filter by channel type
        setEmailReminders(validReminders.filter(r => r && r.channel === 'email'));
        setPushReminders(validReminders.filter(r => r && r.channel === 'push'));
      } catch (error) {
        console.error('Error fetching reminders:', error);
        toast.error('Failed to load reminders');
      } finally {
        setRemindersLoading(false);
      }
    };

    if (studyName) {
      // Hand the draft-load promise to the survey fetch so its per-id name
      // resolution reads the real schedules, not this closure's stale state.
      const draftSchedulesPromise = loadDraftData();
      fetchAllSurveys(draftSchedulesPromise);
      fetchReminders();
    } else {
      setSurveysLoading(false);
      setRemindersLoading(false);
    }
  }, [studyName]);

  // Load schedules from API
  const loadSchedules = async () => {
    try {
      const response = await studyBuilderService.getStudyDraft(studyName);
      if (response.schedules) {
        // Initialize time fields if they don't exist
        const defaultTime = { hours: '12', minutes: '00', period: 'AM' };  // Use local time format
        
        const schedulesWithTime = response.schedules.map(schedule => ({
          ...schedule,
          pushReminderTimers: schedule.pushReminderTimers || [{ ...defaultTime }],
          emailReminderTimers: schedule.emailReminderTimers || [{ ...defaultTime }],
          // FECOA-1236: the Delivery Window is always present, so the day-window
          // is always active — keep the flag true so its Open/Close Day range
          // persists on save even for schedules saved before the consolidation.
          hasAssessmentWindow: true,
          assessmentWindows: normalizeAssessmentWindows(schedule)
        }));
        setSchedules(schedulesWithTime);
      }
    } catch (error) {
      console.error('Error loading schedules:', error);
      toast.error('Failed to load schedules');
    }
  };

  const isValidStartDay = (value) => {
    // check number
    if (Number.isInteger(value) && value >= -365 && value <= 365)
      return true;
    // check text
    const regex = /^-?(?:[1-9]\d*|0)$/;
    if (!regex.test(value))
      return false;
    const val = parseInt(value, 10);
    return val >= -365 && val <= 365;
  }


  // Save schedules to API
  const handleSave = async () => {
    setShowValidation(true);

    // Validate all schedules
    let isValid = true;
    
    // Get a fresh reference to the current study start day
    const currentStudyStartDay = studyStartDay;
    
    // Debug logging
    console.log(`handleSave - Current study start day: ${currentStudyStartDay}`);
    console.log(`Schedules length: ${schedules.length}`);
    
    // Validate study start day first
    if (!isValidStartDay(currentStudyStartDay)) {
      isValid = false;
      toast.error('Study start day is required');
    }
    
    for (let i = 0; i < schedules.length; i++) {
      if (!validateSchedule(schedules[i], i)) {
        isValid = false;
      }
    }

    if (!isValid) {
      return;
    }

    try {
      // Create fresh copies of all data to avoid stale references
      
      // Create a completely fresh list of schedules with the current study start day
      const finalSchedules = schedules.map(schedule => {
        // Make a deep copy to ensure no shared references
        const newSchedule = JSON.parse(JSON.stringify(schedule));
        // Update study start day
        newSchedule.studyStartDay = currentStudyStartDay;
        return newSchedule;
      });
      
      console.log(`Submitting ${finalSchedules.length} schedules with study start day: ${currentStudyStartDay}`);
      console.log(`First schedule study start day: ${finalSchedules[0]?.studyStartDay}`);

      // Before submitting, save linked surveys to the backend if needed
      const linkedSurveySavePromises = [];
      
      for (const schedule of finalSchedules) {
        if (schedule.hasLinkedSurveys && schedule.standardSurveys && schedule.standardSurveys.length > 1) {
          console.log(`Processing linked surveys for schedule: ${JSON.stringify(schedule.standardSurveys)}`);
          
          // First update the ranks of all surveys in this schedule based on their position
          schedule.standardSurveys.forEach((surveyId, index) => {
            // Add rank update to our promise list - lowest index gets lowest rank
            // This MUST happen before updating linked_surveys to ensure rank is set
            const updateRankPromise = surveyService.updateSurveyRank(surveyId, index)
              .catch(error => {
                console.error(`Error updating rank for survey ${surveyId}:`, error);
                return null;
              });
              
            linkedSurveySavePromises.push(updateRankPromise);
          });
          
          // Only the head survey stores ordered followers; clear the rest so
          // parent/child resolution on the participant dashboard stays deterministic.
          const headSurveyId = schedule.standardSurveys[0];
          const followerSurveyIds = schedule.standardSurveys.slice(1);

          linkedSurveySavePromises.push(
            surveyService.updateLinkedSurveys(headSurveyId, followerSurveyIds)
              .catch(error => {
                console.error(`Error updating linked surveys for head survey ${headSurveyId}:`, error);
                return null;
              })
          );

          followerSurveyIds.forEach((surveyId) => {
            linkedSurveySavePromises.push(
              surveyService.updateLinkedSurveys(surveyId, [])
                .catch(error => {
                  console.error(`Error clearing linked surveys for survey ${surveyId}:`, error);
                  return null;
                })
            );
          });
        }
      }
      
      // Wait for all linked survey updates to complete before continuing
      if (linkedSurveySavePromises.length > 0) {
        console.log(`Saving ${linkedSurveySavePromises.length} linked survey updates...`);
        await Promise.all(linkedSurveySavePromises);
        console.log('All linked survey updates completed');
      }

      // Final submission data
      const submissionData = {
        studyStartDay: currentStudyStartDay,
        schedules: finalSchedules,
        arms: arms  // Include global arms array
      };

      // Log the exact data being sent to the API
      console.log('Submission data:', JSON.stringify(submissionData));

      // Submit to API
      await studyBuilderService.updateStudyDraft(studyName, submissionData);

      // Update local state with the submitted data
      setSchedules(finalSchedules);
      toast.success('Schedules saved successfully');
      
      // Redirect to next step
      navigate(`/studybuilder/sites/${studyName}`);
    } catch (error) {
      console.error('Error saving schedules:', error);
      
      // Check if the error contains validation errors from the backend
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        // Handle validation errors
        if (errorData.config) {
          // Display specific config validation errors
          const configErrors = Array.isArray(errorData.config) ? errorData.config : [errorData.config];
          configErrors.forEach(err => toast.error(err));
        } else if (errorData.detail) {
          toast.error(errorData.detail);
        } else if (errorData.non_field_errors) {
          errorData.non_field_errors.forEach(err => toast.error(err));
        } else {
          toast.error('Failed to save schedules');
        }
      } else {
        toast.error('Failed to save schedules');
      }
    }
  };

  // Add a new effect to handle schedule selection after schedules are loaded
  useEffect(() => {
    if (schedules.length > 0 && selectedScheduleIndex === null) {
      handleScheduleSelect(0);
    }
  }, [schedules, selectedScheduleIndex]);

  // Helpers for arms
  const isDuplicateName = (array, name, currIndex = -1) => {
    return array.some((item, i) => {
      if (i === currIndex) return false;
      const itemName = typeof item === 'string' ? item : item.name;
      return itemName.trim().toLowerCase() === name.trim().toLowerCase();
    });
  };

  const handleAddArm = () => {
    if (arms.some((a) => a.trim() === '')) {
      toast.error('Please fill in the empty arm name first.');
      return;
    }
    const nextLetter = String.fromCharCode('A'.charCodeAt(0) + arms.length);
    setArms([...arms, `Arm ${nextLetter}`]);
  };

  const handleRemoveArm = (index) => {
    const removedArm = arms[index];
    const newArms = arms.filter((_, i) => i !== index);
    setArms(newArms);
    
    // Also remove the deleted arm from all schedules' selected arms
    const updatedSchedules = schedules.map(schedule => ({
      ...schedule,
      arms: schedule.arms.filter(selectedArm => selectedArm !== removedArm)
    }));
    setSchedules(updatedSchedules);
    
    // If currently editing a schedule, update its arms too
    if (editingSchedule && editingSchedule.arms.includes(removedArm)) {
      const updatedEditingSchedule = {
        ...editingSchedule,
        arms: editingSchedule.arms.filter(selectedArm => selectedArm !== removedArm)
      };
      setEditingSchedule(updatedEditingSchedule);
    }
  };

  const handleArmChange = (index, value) => {
    if (value.trim() !== '' && isDuplicateName(arms, value, index)) {
      toast.error('An arm with this name already exists');
      return;
    }
    const newArms = [...arms];
    newArms[index] = value;
    setArms(newArms);
  };

  // Schedules
  const handleAddSchedule = () => {
    const newSchedule = createNewSchedule();
    setSchedules([...schedules, newSchedule]);
    setSelectedScheduleIndex(schedules.length);
    setEditingSchedule(newSchedule);
  };

  const handleRemoveSchedule = (index) => {
    const newSchedules = schedules.filter((_, i) => i !== index);
    setSchedules(newSchedules);
    if (selectedScheduleIndex === index) {
      setSelectedScheduleIndex(null);
      // Initialize with a new empty schedule instead of null
      setEditingSchedule(createNewSchedule());
    }
  };

  const handleScheduleSelect = (idx) => {
    const schedule = schedules[idx];
    if (!schedule) return;

    const defaultTime = { hours: '12', minutes: '00', period: 'AM' };  // Use local time format
    
    // Hydrate dailyStartDay + dailyDuration for implied-daily types.
    // Prefer top-level fields (persisted as source of truth); fall back to stage math
    // for legacy drafts saved before these fields were persisted.
    const hasValue = (v) => v !== null && v !== undefined && v !== '';
    let dailyDuration = hasValue(schedule.dailyDuration) ? String(schedule.dailyDuration) : '';
    let dailyStartDay = hasValue(schedule.dailyStartDay) ? String(schedule.dailyStartDay) : '';
    if (isImpliedDailyType(schedule.type) && schedule.stages?.[0]) {
      const stage = schedule.stages[0];
      if (!hasValue(dailyStartDay) && hasValue(stage.startDay)) {
        dailyStartDay = String(stage.startDay);
      }
      if (!hasValue(dailyDuration) && hasValue(stage.startDay) && hasValue(stage.endDay)) {
        dailyDuration = String(parseInt(stage.endDay, 10) - parseInt(stage.startDay, 10) + 1);
      }
    }
    if (isImpliedDailyType(schedule.type) && !hasValue(dailyStartDay)) {
      dailyStartDay = String(hasValue(studyStartDay) ? studyStartDay : '1');
    }

    setEditingSchedule({
      ...schedule,
      dailyDuration,
      dailyStartDay,
      pushReminderTimers: schedule.pushReminderTimers || [{ ...defaultTime }],
      emailReminderTimers: schedule.emailReminderTimers || [{ ...defaultTime }],
      // FECOA-1236: Delivery Window is always-on, so keep the day-window active.
      hasAssessmentWindow: true,
      assessmentWindows: normalizeAssessmentWindows(schedule),
      // FECOA-1236: seed the once-a-day single timepoint when a timed schedule has
      // no timepoints yet, so the editor shows the same window that will be saved.
      timepoints:
        supportsTimepoints(schedule.type) &&
        (!schedule.timepoints || schedule.timepoints.length === 0)
          ? [makeOnceTimepoint(null, schedule.standardSurveys)]
          : (schedule.timepoints || [])
    });
    setSelectedScheduleIndex(idx);
  };

  const handleScheduleChange = (field, value) => {
    if (!selectedScheduleIndex && selectedScheduleIndex !== 0) return;

    // Create updated schedule with special field handling
    const updated = { ...editingSchedule };

    if (field === 'enablePushReminders') {
      updated.enablePushReminders = value;
      if (!value) {
        updated.pushReminders = [];
      }
    } else if (field === 'enableEmailReminders') {
      updated.enableEmailReminders = value;
      if (!value) {
        updated.emailReminders = [];
      }
    } else if (field === 'enableMissingForms') {
      updated.enableMissingForms = value;
      if (!value) {
        updated.missingForms = [];
      }
    } else if (field === 'standardSurveys') {
      updated.standardSurveys = value;
      
      // Mark that we have linked surveys but don't update backend yet
      // This will be done when saving the entire schedule
      updated.hasLinkedSurveys = value && value.length > 1;
      
      // Immediately update the linked status in the backend
      if (value && Array.isArray(value)) {
        // If there's more than one survey, set linked status to true, otherwise false
        const shouldBeLinked = value.length > 1;
        
        // Call the API to update the surveys' linked status
        surveyService.updateSurveysLinkedStatus(value, shouldBeLinked)
          .then(() => {
            console.log(`Updated linked status for ${value.length} surveys: is_linked=${shouldBeLinked}`);
          })
          .catch(error => {
            console.error('Error updating survey linked status:', error);
            toast.error('Failed to update survey link status');
          });
      }
    } else if (field === 'hasAssessmentWindow') {
      updated.hasAssessmentWindow = value;
      if (!value) {
        updated.assessmentWindows = [];
      } else if (!Array.isArray(updated.assessmentWindows) || updated.assessmentWindows.length === 0) {
        // Seed the first window so the form has something to render when enabled
        updated.assessmentWindows = [makeDefaultWindow()];
      }
    } else {
      updated[field] = value;
    }

    // Special handling for schedule type changes
    if (field === 'type') {
      if (isTimerType(value)) {
        updated.assessmentTimers = updated.assessmentTimers || [{
          enabled: true,
          minutes: 15,
          addAlarm: false
        }];
      } else {
        // Timers only belong to the "- Timer" types — clear any stale timers so
        // they aren't hidden-but-saved (FECOA-1625).
        updated.assessmentTimers = [];
      }
      // Intra-day timepoints only apply to Scheduled / Daily Assessment — clear
      // stale timepoints for Patient Prompted so they aren't hidden-but-saved
      // (FECOA-1236). Those types fall back to once-a-day / all-day availability.
      if (!supportsTimepoints(value)) {
        updated.timepoints = [];
      } else if (!Array.isArray(updated.timepoints) || updated.timepoints.length === 0) {
        // Switching into a timed type with no timepoints defaults to once-a-day
        // (a single unnamed window) so the Delivery Window renders immediately.
        updated.timepoints = [makeOnceTimepoint(null, updated.standardSurveys)];
      }
      // FECOA-1236: every type keeps the Delivery Window day range, so ensure a
      // single day-window entry exists rather than clearing it.
      updated.hasAssessmentWindow = true;
      if (!Array.isArray(updated.assessmentWindows) || updated.assessmentWindows.length === 0) {
        updated.assessmentWindows = [makeDefaultWindow()];
      }
      // Implied-daily types (PPD, Daily Assessment) imply daily cadence — set a default stage
      if (isImpliedDailyType(value)) {
        const defaultStart = updated.dailyStartDay || studyStartDay || '1';
        updated.dailyStartDay = defaultStart;
        updated.stages = [{
          name: 'Daily',
          startDay: defaultStart,
          endDay: updated.dailyDuration
            ? String(parseInt(defaultStart, 10) + parseInt(updated.dailyDuration, 10) - 1)
            : '',
          frequency: { value: '1', unit: 'days' }
        }];
        if (!updated.dailyDuration) {
          updated.dailyDuration = '';
        }
      }
    }

    setEditingSchedule(updated);

    // Update the schedules array
    const newSchedules = [...schedules];
    newSchedules[selectedScheduleIndex] = updated;
    setSchedules(newSchedules);
  };

  // Update a single field on the assessment window at `index`
  const updateAssessmentWindow = (index, field, value) => {
    const windows = [...(editingSchedule.assessmentWindows || [])];
    windows[index] = { ...windows[index], [field]: value };
    handleScheduleChange('assessmentWindows', windows);
  };

  // Dedicated handler for study start day changes
  const handleStudyStartDayChange = (newValue) => {
    // Sanitize: only digits with an optional single leading minus. Strips letters,
    // symbols, and trailing/embedded dashes so "3-2" → "32" and "--5" → "-5".
    const raw = String(newValue ?? '');
    if (raw === '') {
      setStudyStartDay('');
      return;
    }
    const stripped = raw.replace(/[^\d-]/g, '');
    const isNegative = stripped.startsWith('-');
    const digits = stripped.replace(/-/g, '');
    const cleaned = isNegative ? `-${digits}` : digits;
    setStudyStartDay(cleaned);
  };

  // This function will be called when the study start day input loses focus
  const handleStudyStartDayBlur = () => {
    console.log(`Updating schedules with study start day: ${studyStartDay}`);
    
    // Then update all schedules (create fresh copies to avoid reference issues)
    const freshSchedules = schedules.map(schedule => ({
      ...schedule,
      studyStartDay: studyStartDay
    }));
    
    // Set the fresh schedules to ensure no stale references
    setSchedules(freshSchedules);
    
    // If there's a current schedule being edited, update it as well
    if (selectedScheduleIndex !== null) {
      const freshEditingSchedule = {
        ...editingSchedule,
        studyStartDay: studyStartDay
      };
      setEditingSchedule(freshEditingSchedule);
    }
  };

  // Helper function for time field changes
  const handleTimeChange = (reminderType, index, field, value) => {
    const timersField = reminderType === 'push' ? 'pushReminderTimers' : 'emailReminderTimers';
    const updatedTimers = [...editingSchedule[timersField]];
    
    // If we're receiving a complete UTC time object (from TimeInput)
    if (field === 'utcTime') {
      updatedTimers[index] = value;
    } else {
      // For backward compatibility or direct field changes
      updatedTimers[index] = { 
        ...updatedTimers[index],
        [field]: value 
      };
    }
    
    // Update the schedule with the modified timers
    handleScheduleChange(timersField, updatedTimers);
  };

  // Add a new timer
  const handleAddTimer = (reminderType) => {
    const timersField = reminderType === 'push' ? 'pushReminderTimers' : 'emailReminderTimers';
    const updatedTimers = [...editingSchedule[timersField], { hours: '12', minutes: '00', period: 'AM' }];
    handleScheduleChange(timersField, updatedTimers);
  };

  // Remove a timer
  const handleRemoveTimer = (reminderType, index) => {
    const timersField = reminderType === 'push' ? 'pushReminderTimers' : 'emailReminderTimers';
    const updatedTimers = editingSchedule[timersField].filter((_, i) => i !== index);
    if (updatedTimers.length > 0) {
      handleScheduleChange(timersField, updatedTimers);
    } else {
      toast.error(`Must have at least one ${reminderType} reminder time`);
    }
  };

  // Time input component with debug safeguards
  const TimeInput = ({ label, time, onChange, onRemove, showRemove = true }) => {
    // Keep track of UTC and local time separately
    const [localTime, setLocalTime] = React.useState(() => {
      // If time is null/undefined, use default local time
      if (!time) {
        return { hours: '12', minutes: '00', period: 'PM' };
      }
      
      // If time is in UTC format, convert to local for display
      if (time.period === 'UTC') {
        // Create a date using UTC time
        const utcDate = new Date();
        utcDate.setUTCHours(
          parseInt(time.hours, 10),
          parseInt(time.minutes, 10),
          0, 0
        );
        
        // Get local time components
        let localHours = utcDate.getHours();
        const localPeriod = localHours >= 12 ? 'PM' : 'AM';
        
        // Convert to 12-hour format
        localHours = localHours % 12;
        if (localHours === 0) localHours = 12;
        
        return {
          hours: localHours.toString().padStart(2, '0'),
          minutes: utcDate.getMinutes().toString().padStart(2, '0'),
          period: localPeriod
        };
      }
      
      // If it's already in local format, use it directly
      return { ...time };
    });
    
    // When local time changes, convert to UTC and update parent
    const handleLocalTimeChange = (field, value) => {
      // Update local state
      const updatedLocalTime = {
        ...localTime,
        [field]: value
      };
      setLocalTime(updatedLocalTime);
      
      // Convert local time to UTC
      let hours = parseInt(updatedLocalTime.hours, 10);
      const minutes = parseInt(updatedLocalTime.minutes, 10);
      
      // Convert to 24-hour format
      if (updatedLocalTime.period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (updatedLocalTime.period === 'AM' && hours === 12) {
        hours = 0;
      }
      
      // Create a date with the local time
      const localDate = new Date();
      localDate.setHours(hours, minutes, 0, 0);
      
      // Convert to UTC
      const utcTime = {
        hours: localDate.getUTCHours().toString().padStart(2, '0'),
        minutes: localDate.getUTCMinutes().toString().padStart(2, '0'),
        period: 'UTC'
      };
      
      // Only call onChange with the converted UTC time
      onChange('utcTime', utcTime);
    };

    const timeSelectClass = "flex-1 py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-800";

    return (
      <div className="flex items-center gap-3">
        {label && <label className="text-sm text-gray-600">{label}</label>}
        <SafeSelect
          value={localTime.hours}
          onChange={(e) => handleLocalTimeChange('hours', e.target.value)}
          className={timeSelectClass}
        >
          {Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')).map(hour => (
            <option key={hour} value={hour}>{hour}</option>
          ))}
        </SafeSelect>
        <span className="text-gray-400 font-medium text-lg">:</span>
        <SafeSelect
          value={localTime.minutes}
          onChange={(e) => handleLocalTimeChange('minutes', e.target.value)}
          className={timeSelectClass}
        >
          {['00', '15', '30', '45'].map(min => (
            <option key={min} value={min}>{min}</option>
          ))}
        </SafeSelect>
        <SafeSelect
          value={localTime.period}
          onChange={(e) => handleLocalTimeChange('period', e.target.value)}
          className={timeSelectClass}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </SafeSelect>
        {showRemove && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-red-600 hover:text-red-800"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  // Add Assessment Timer
  const handleAddAssessmentTimer = () => {
    const updatedSchedule = {
      ...editingSchedule,
      assessmentTimers: [
        ...(editingSchedule.assessmentTimers || []),
        {
          enabled: true,
          minutes: 15,
          addAlarm: false
        }
      ]
    };
    setEditingSchedule(updatedSchedule);

    // Update schedules array
    const newSchedules = [...schedules];
    newSchedules[selectedScheduleIndex] = updatedSchedule;
    setSchedules(newSchedules);
  };

  // Update Assessment Timer
  const handleUpdateAssessmentTimer = (index, field, value) => {
    const newTimers = [...(editingSchedule.assessmentTimers || [])];
    newTimers[index] = {
      ...newTimers[index],
      [field]: value
    };
    
    const updatedSchedule = {
      ...editingSchedule,
      assessmentTimers: newTimers
    };
    setEditingSchedule(updatedSchedule);

    // Update schedules array
    const newSchedules = [...schedules];
    newSchedules[selectedScheduleIndex] = updatedSchedule;
    setSchedules(newSchedules);
  };

  // Remove Assessment Timer
  const handleRemoveAssessmentTimer = (index) => {
    const updatedSchedule = {
      ...editingSchedule,
      assessmentTimers: (editingSchedule.assessmentTimers || []).filter((_, i) => i !== index)
    };
    setEditingSchedule(updatedSchedule);

    // Update schedules array
    const newSchedules = [...schedules];
    newSchedules[selectedScheduleIndex] = updatedSchedule;
    setSchedules(newSchedules);
  };

  // Timepoints (intra-day delivery)
  const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

  // FECOA-1236 AC5: new timepoints default to Morning, Afternoon, Evening, then
  // "Timepoint N" for the 4th onward. `index` is the zero-based position of the
  // timepoint being added.
  const TIMEPOINT_DEFAULT_NAMES = ['Morning', 'Afternoon', 'Evening'];
  const defaultTimepointName = (index) =>
    TIMEPOINT_DEFAULT_NAMES[index] || `Timepoint ${index + 1}`;

  const makeTimepoint = (index) => ({
    id: `tp_${Date.now()}_${index}`,
    name: defaultTimepointName(index),
    target_time: '08:00',
    window_minutes: 60,
    surveys: [],
    enforce_order: true, // FECOA-1333: strict within-timepoint ordering, on by default for new timepoints
    time_gated: true, // FECOA-1824: strict daily slot, on by default for new timepoints (recall-bias rule)
  });

  // FECOA-1236 AC8: stash the entered timepoints when the user switches to "Once a
  // day" so toggling back to "Multiple times a day" restores them instead of
  // starting from an empty list.
  const stashedTimepointsRef = useRef([]);
  // FECOA-1333: likewise stash the once-a-day window when switching to "Multiple
  // times a day" so toggling back keeps its id, times, and enforce_order.
  const stashedOnceTimepointRef = useRef(null);

  // FECOA-1522: respondent type is now an instrument-level property on the
  // Survey (set in the Assessment Builder) and chosen per-schedule via the
  // schedule-level Respondent Type control, which filters the survey picker.
  // The old per-survey global setter was removed.

  // Build the single unnamed timepoint that represents "Once a day". Surveys
  // default to the full schedule survey list (no per-timepoint subsetting in
  // once-a-day mode); they're re-synced to all surveys on save.
  //
  // A once-a-day task must be completable any time that day, so the default is
  // an all-day window (00:00, 1440 min) — matching the backend's synthetic
  // "All Day" fallback in resolve_timepoints. The previous 08:00/60 default
  // opened only an 08:00-09:00 slot, so a participant's survey flipped to
  // "Missed" by mid-morning even though they hadn't missed the day.
  const makeOnceTimepoint = (prev, standardSurveys) => ({
    id: prev?.id || `tp_once_${Date.now()}`,
    name: '',
    target_time: prev?.target_time || '00:00',
    window_minutes: prev?.window_minutes ?? 1440,
    // FECOA-1333: new schedules default to strict ordering; rebuilding an
    // existing once window must keep its saved flag (including saved-off).
    enforce_order: prev ? !!prev.enforce_order : true,
    // FECOA-1824: once-a-day has no time-gate control (its window already spans
    // the day and the Open/Close Day range provides the catch-up), but a saved
    // flag must survive the once/multiple round-trip.
    time_gated: prev ? !!prev.time_gated : false,
    surveys: (standardSurveys ?? editingSchedule.standardSurveys ?? []).map(String),
  });

  const handleSetDeliveryMode = (mode) => {
    const tps = editingSchedule.timepoints || [];
    if (mode === 'once') {
      // Leaving "Multiple": stash the entered timepoints so toggling back
      // restores them (AC8).
      if (!isOnceDeliveryMode(editingSchedule)) {
        stashedTimepointsRef.current = tps;
      }
      const prevOnce = isOnceDeliveryMode(editingSchedule)
        ? tps[0]
        : stashedOnceTimepointRef.current;
      handleScheduleChange('timepoints', [makeOnceTimepoint(prevOnce)]);
    } else {
      // Leaving "Once": stash the once window so toggling back restores it
      // (FECOA-1333) instead of minting a fresh flagless timepoint.
      if (isOnceDeliveryMode(editingSchedule) && tps[0]) {
        stashedOnceTimepointRef.current = tps[0];
      }
      // Switching to "Multiple": restore the stashed timepoints (AC8) or seed a
      // single default (named) timepoint so the editor has a row to render.
      const restored = stashedTimepointsRef.current;
      handleScheduleChange(
        'timepoints',
        restored && restored.length > 0 ? restored : [makeTimepoint(0)],
      );
    }
  };

  // Edit the single once-a-day window (target time / window length). Materializes
  // the timepoint if it doesn't exist yet so what's shown is what's saved.
  const handleOnceWindowChange = (field, val) => {
    const base = (editingSchedule.timepoints || [])[0] || makeOnceTimepoint(null);
    handleScheduleChange('timepoints', [{ ...base, name: '', [field]: val }]);
  };

  const handleAddTimepoint = () => {
    const current = editingSchedule.timepoints || [];
    const next = [...current, makeTimepoint(current.length)];
    handleScheduleChange('timepoints', next);
  };

  const handleTimepointChange = (index, field, val) => {
    const next = [...(editingSchedule.timepoints || [])];
    next[index] = { ...next[index], [field]: val };
    handleScheduleChange('timepoints', next);
  };

  const handleRemoveTimepoint = (index) => {
    const next = (editingSchedule.timepoints || []).filter((_, i) => i !== index);
    handleScheduleChange('timepoints', next);
  };

  // Stages
  const handleStageChange = (stageIndex, field, val) => {
    const updatedStages = [...editingSchedule.stages];
    const stg = { ...updatedStages[stageIndex] };

    if (field === 'frequencyValue') {
      // Prevent frequency value of 0 - enforce minimum of 1
      if (val !== '' && parseInt(val, 10) < 1) {
        toast.error('Frequency must be at least 1');
        return;
      }
      const parsedValue = val === '' ? '' : val;
      stg.frequency = { ...stg.frequency, value: parsedValue };
    } else if (field === 'frequencyUnit') {
      stg.frequency = { ...stg.frequency, unit: val };
    } else {
      stg[field] = val;
    }

    updatedStages[stageIndex] = stg;
    handleScheduleChange('stages', updatedStages);
  };

  // Validation
  const validateSchedule = (schedule, idx) => {
    let isValid = true;
    const errors = [];

    if (!schedule.name) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: Name is required`);
    }

    // Check for duplicate schedule names
    if (schedule.name) {
      // Get all schedule names except the current one
      const otherSchedules = schedules.filter((_, i) => i !== idx);
      const duplicateName = otherSchedules.some(s => s.name.trim().toLowerCase() === schedule.name.trim().toLowerCase());
      if (duplicateName) {
        isValid = false;
        errors.push(`Schedule ${idx + 1}: A schedule with name "${schedule.name}" already exists`);
      }
    }

    // Validate studyStartDay
    if (!isValidStartDay(studyStartDay)) {
      isValid = false;
      errors.push(`Study start day is required`);
    }

    // Validate arms - stronger validation to ensure at least one arm is selected
    if (!schedule.arms || !Array.isArray(schedule.arms) || schedule.arms.length === 0 || schedule.arms.every(arm => !arm || arm.trim() === '')) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one arm must be selected`);
    }

    if (schedule.enablePushReminders && (!schedule.pushReminders || schedule.pushReminders.length === 0)) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one push reminder must be selected when push reminders are enabled`);
    }

    if (schedule.enableEmailReminders && (!schedule.emailReminders || schedule.emailReminders.length === 0)) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one email reminder must be selected when email reminders are enabled`);
    }

    if (schedule.enableMissingForms && (!schedule.missingForms || !schedule.missingForms.length)) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: A missing form must be selected when missing forms are enabled`);
    }

    if (!schedule.standardSurveys || schedule.standardSurveys.length === 0) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one survey must be selected`);
    }

    if (schedule.type && schedule.type.includes('Patient Prompted - Timer') && (!schedule.assessmentTimers || schedule.assessmentTimers.length === 0)) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one assessment timer is required for ${schedule.type} type`);
    }

    if (!schedule.siteType) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: Site type is required`);
    }

    if (!schedule.type) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: Schedule type is required`);
    }

    // Timepoints: if any are defined, each must be well-formed and only reference
    // surveys present in standardSurveys. Empty list is fine — backend falls back
    // to a single All Day timepoint.
    if (Array.isArray(schedule.timepoints) && schedule.timepoints.length > 0) {
      const surveyPool = new Set((schedule.standardSurveys || []).map(String));
      const seenIds = new Set();
      const onceMode = isOnceDeliveryMode(schedule);
      schedule.timepoints.forEach((tp, tpIdx) => {
        // FECOA-1676: use the names the user actually typed so the toast points at
        // the right schedule/timepoint instead of opaque positional numbers.
        const schedPart = (schedule.name || '').trim() ? `Schedule "${schedule.name.trim()}"` : `Schedule ${idx + 1}`;
        const tpPart = (tp.name || '').trim() ? `timepoint "${tp.name.trim()}"` : `timepoint ${tpIdx + 1}`;
        const label = `${schedPart}, ${tpPart}`;
        // Once-a-day uses a single unnamed timepoint (Name hidden); only multiple
        // times a day requires a per-timepoint name (FECOA-1236).
        if (!onceMode && (!tp.name || !tp.name.trim())) {
          isValid = false;
          errors.push(`${label}: name is required`);
        }
        if (!tp.target_time || !HHMM_RE.test(tp.target_time)) {
          isValid = false;
          errors.push(`${label}: target time must be HH:MM (24h)`);
        }
        const wm = parseInt(tp.window_minutes, 10);
        if (!Number.isFinite(wm) || wm <= 0 || wm > 1440) {
          isValid = false;
          errors.push(`${label}: window length must be between 1 and 1440 minutes`);
        }
        // Once-a-day delivers all schedule surveys (no per-timepoint subsetting);
        // the survey selector is only shown for multiple times a day.
        if (!onceMode) {
          if (!Array.isArray(tp.surveys) || tp.surveys.length === 0) {
            isValid = false;
            errors.push(`${label}: pick at least one survey`);
          } else {
            const extras = tp.surveys.filter((s) => !surveyPool.has(String(s)));
            if (extras.length) {
              isValid = false;
              errors.push(`${label}: surveys must be a subset of the schedule's survey list`);
            }
          }
        }
        if (tp.id) {
          if (seenIds.has(tp.id)) {
            isValid = false;
            errors.push(`${label}: duplicate timepoint id`);
          }
          seenIds.add(tp.id);
        }
      });
    }

    // FECOA-1236: the Delivery Window is a single Open/Close Day range driven by
    // <select>s with safe defaults, so there is nothing to validate here. The old
    // window Name / Start Time / End Time fields were removed in the consolidation.

    // Validate stages — implied-daily types (PPD, Daily Assessment) only need start day + duration
    const stagesSkipValidation = isImpliedDailyType(schedule.type);
    if (stagesSkipValidation) {
      if (schedule.dailyStartDay === '' || schedule.dailyStartDay == null) {
        isValid = false;
        errors.push(`Schedule ${idx + 1}: Start day is required`);
      } else if (parseInt(schedule.dailyStartDay, 10) < parseInt(studyStartDay || '0', 10)) {
        isValid = false;
        errors.push(`Schedule ${idx + 1}: Start day must be on or after the study start day (${studyStartDay || '0'})`);
      }
      if (!schedule.dailyDuration || parseInt(schedule.dailyDuration, 10) < 1) {
        isValid = false;
        errors.push(`Schedule ${idx + 1}: Schedule duration (days) is required`);
      }
    } else if (!schedule.stages || schedule.stages.length === 0) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one stage is required`);
    } else {
      schedule.stages.forEach((stage, index) => {
        if (!stage.name) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Name is required`);
        }

        const stageNames = schedule.stages.map(s => s.name);
        const duplicateNames = stageNames.filter((name, i, arr) =>
          name && arr.indexOf(name) !== i
        );

        if (duplicateNames.includes(stage.name)) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Stage name "${stage.name}" is not unique. Stage names must be unique within a schedule.`);
        }

        if (!isValidStartDay(stage.startDay)) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Start day is required`);
        }

        if (!stage.endDay) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: End day is required`);
        } else if (parseFloat(stage.endDay) < 0) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: End day cannot be negative`);
        } else if (!Number.isInteger(parseFloat(stage.endDay))) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: End day must be a whole number`);
        }

        if (parseInt(stage.startDay) > parseInt(stage.endDay)) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Start day must be less than or equal to end day`);
        }
        if (parseInt(stage.startDay) < parseInt(studyStartDay)) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Start day must be greater than or equal to study start day`);
        }
        // Validate stage overlap: each stage's start day must be after the previous stage's end day
        if (index > 0) {
          const prevStage = schedule.stages[index - 1];
          if (prevStage.endDay !== '' && stage.startDay !== '' && parseInt(stage.startDay) <= parseInt(prevStage.endDay)) {
            isValid = false;
            errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Start day must be after Stage ${index}'s end day (${prevStage.endDay}) to prevent overlapping stages`);
          }
        }
        if (!stage.frequency || !stage.frequency.value || parseInt(stage.frequency.value, 10) < 1) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Frequency must be greater than 0`);
        }
        
        if (stage.frequency && stage.frequency.value && parseInt(stage.frequency.value, 10) >= 1 && stage.startDay && stage.endDay) {
          const startDay = parseInt(stage.startDay, 10);
          const endDay = parseInt(stage.endDay, 10);
          const stageDuration = endDay - startDay + 1;
          
          let frequencyInDays = parseInt(stage.frequency.value, 10);
          
          const frequencyUnits = {
            weeks: 7,
            months: 30,
            years: 365
          };
          
          if (stage.frequency.unit in frequencyUnits) {
            frequencyInDays *= frequencyUnits[stage.frequency.unit];
          }
          
          if (frequencyInDays > stageDuration) {
            isValid = false;
            errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Frequency (${stage.frequency.value} ${stage.frequency.unit}) exceeds the stage duration (${stageDuration} days). The frequency must fit within the stage period.`);
          }
        }
      });
    }

    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
    }

    return isValid;
  };

  // Validation without duplicate name checks (used by handleSubmit)
  const validateScheduleExceptDuplicateNames = (schedule, idx) => {
    let isValid = true;
    const errors = [];

    if (!schedule.name) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: Name is required`);
    }

    // Validate studyStartDay
    if (!isValidStartDay(studyStartDay)) {
      isValid = false;
      errors.push(`Study start day is required`);
    }

    if (schedule.enablePushReminders && (!schedule.pushReminders || schedule.pushReminders.length === 0)) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one push reminder must be selected when push reminders are enabled`);
    }

    if (schedule.enableEmailReminders && (!schedule.emailReminders || schedule.emailReminders.length === 0)) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one email reminder must be selected when email reminders are enabled`);
    }

    if (schedule.enableMissingForms && (!schedule.missingForms || !schedule.missingForms.length)) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: A missing form must be selected when missing forms are enabled`);
    }

    if (!schedule.standardSurveys || schedule.standardSurveys.length === 0) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one survey must be selected`);
    }

    if (!schedule.arms || schedule.arms.length === 0) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one arm must be selected`);
    }

    if (schedule.type && schedule.type.includes('Patient Prompted - Timer') && (!schedule.assessmentTimers || schedule.assessmentTimers.length === 0)) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one assessment timer is required for ${schedule.type} type`);
    }

    if (!schedule.siteType) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: Site type is required`);
    }

    if (!schedule.type) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: Schedule type is required`);
    }

    // Timepoints: if any are defined, each must be well-formed and only reference
    // surveys present in standardSurveys. Empty list is fine — backend falls back
    // to a single All Day timepoint.
    if (Array.isArray(schedule.timepoints) && schedule.timepoints.length > 0) {
      const surveyPool = new Set((schedule.standardSurveys || []).map(String));
      const seenIds = new Set();
      const onceMode = isOnceDeliveryMode(schedule);
      schedule.timepoints.forEach((tp, tpIdx) => {
        // FECOA-1676: use the names the user actually typed so the toast points at
        // the right schedule/timepoint instead of opaque positional numbers.
        const schedPart = (schedule.name || '').trim() ? `Schedule "${schedule.name.trim()}"` : `Schedule ${idx + 1}`;
        const tpPart = (tp.name || '').trim() ? `timepoint "${tp.name.trim()}"` : `timepoint ${tpIdx + 1}`;
        const label = `${schedPart}, ${tpPart}`;
        // Once-a-day uses a single unnamed timepoint (Name hidden); only multiple
        // times a day requires a per-timepoint name (FECOA-1236).
        if (!onceMode && (!tp.name || !tp.name.trim())) {
          isValid = false;
          errors.push(`${label}: name is required`);
        }
        if (!tp.target_time || !HHMM_RE.test(tp.target_time)) {
          isValid = false;
          errors.push(`${label}: target time must be HH:MM (24h)`);
        }
        const wm = parseInt(tp.window_minutes, 10);
        if (!Number.isFinite(wm) || wm <= 0 || wm > 1440) {
          isValid = false;
          errors.push(`${label}: window length must be between 1 and 1440 minutes`);
        }
        // Once-a-day delivers all schedule surveys (no per-timepoint subsetting);
        // the survey selector is only shown for multiple times a day.
        if (!onceMode) {
          if (!Array.isArray(tp.surveys) || tp.surveys.length === 0) {
            isValid = false;
            errors.push(`${label}: pick at least one survey`);
          } else {
            const extras = tp.surveys.filter((s) => !surveyPool.has(String(s)));
            if (extras.length) {
              isValid = false;
              errors.push(`${label}: surveys must be a subset of the schedule's survey list`);
            }
          }
        }
        if (tp.id) {
          if (seenIds.has(tp.id)) {
            isValid = false;
            errors.push(`${label}: duplicate timepoint id`);
          }
          seenIds.add(tp.id);
        }
      });
    }

    // FECOA-1236: the Delivery Window is a single Open/Close Day range driven by
    // <select>s with safe defaults, so there is nothing to validate here. The old
    // window Name / Start Time / End Time fields were removed in the consolidation.

    // Validate stages — implied-daily types (PPD, Daily Assessment) only need start day + duration
    const stagesSkipValidation2 = isImpliedDailyType(schedule.type);
    if (stagesSkipValidation2) {
      if (schedule.dailyStartDay === '' || schedule.dailyStartDay == null) {
        isValid = false;
        errors.push(`Schedule ${idx + 1}: Start day is required`);
      } else if (parseInt(schedule.dailyStartDay, 10) < parseInt(studyStartDay || '0', 10)) {
        isValid = false;
        errors.push(`Schedule ${idx + 1}: Start day must be on or after the study start day (${studyStartDay || '0'})`);
      }
      if (!schedule.dailyDuration || parseInt(schedule.dailyDuration, 10) < 1) {
        isValid = false;
        errors.push(`Schedule ${idx + 1}: Schedule duration (days) is required`);
      }
    } else if (!schedule.stages || schedule.stages.length === 0) {
      isValid = false;
      errors.push(`Schedule ${idx + 1}: At least one stage is required`);
    } else {
      schedule.stages.forEach((stage, index) => {
        if (!stage.name) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Name is required`);
        }
        
        const stageNames = schedule.stages.map(s => s.name);
        const duplicateNames = stageNames.filter((name, i, arr) => 
          name && arr.indexOf(name) !== i
        );
        
        if (duplicateNames.includes(stage.name)) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Stage name "${stage.name}" is not unique. Stage names must be unique within a schedule.`);
        }
        
        if (!isValidStartDay(stage.startDay)) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Start day is required`);
        } 
        
        if (!stage.endDay) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: End day is required`);
        } else if (parseFloat(stage.endDay) < 0) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: End day cannot be negative`);
        } else if (!Number.isInteger(parseFloat(stage.endDay))) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: End day must be a whole number`);
        }
        
        if (parseInt(stage.startDay) > parseInt(stage.endDay)) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Start day must be less than or equal to end day`);
        }
        if (parseInt(stage.startDay) < parseInt(studyStartDay)) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Start day must be greater than or equal to study start day`);
        }
        // Validate stage overlap: each stage's start day must be after the previous stage's end day
        if (index > 0) {
          const prevStage = schedule.stages[index - 1];
          if (prevStage.endDay !== '' && stage.startDay !== '' && parseInt(stage.startDay) <= parseInt(prevStage.endDay)) {
            isValid = false;
            errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Start day must be after Stage ${index}'s end day (${prevStage.endDay}) to prevent overlapping stages`);
          }
        }
        if (!stage.frequency || !stage.frequency.value || parseInt(stage.frequency.value, 10) < 1) {
          isValid = false;
          errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Frequency must be greater than 0`);
        }
        
        if (stage.frequency && stage.frequency.value && parseInt(stage.frequency.value, 10) >= 1 && stage.startDay && stage.endDay) {
          const startDay = parseInt(stage.startDay, 10);
          const endDay = parseInt(stage.endDay, 10);
          const stageDuration = endDay - startDay + 1;
          
          let frequencyInDays = parseInt(stage.frequency.value, 10);
          
          const frequencyUnits = {
            weeks: 7,
            months: 30,
            years: 365
          };
          
          if (stage.frequency.unit in frequencyUnits) {
            frequencyInDays *= frequencyUnits[stage.frequency.unit];
          }
          
          if (frequencyInDays > stageDuration) {
            isValid = false;
            errors.push(`Schedule ${idx + 1}, Stage ${index + 1}: Frequency (${stage.frequency.value} ${stage.frequency.unit}) exceeds the stage duration (${stageDuration} days). The frequency must fit within the stage period.`);
          }
        }
      });
    }

    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
    }

    return isValid;
  };

  const handleSubmit = async (e, { afterSave } = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    setShowValidation(true);

    // Capture the current study start day value before any async operations
    const currentStudyStartDay = studyStartDay;
    console.log(`Final submit - using study start day: ${currentStudyStartDay}`);

    // Build the final schedules array for validation AND submission
    // This ensures we validate the ACTUAL data being saved, not stale state
    let schedulesToValidate = [...schedules];
    
    // Persist the currently editing schedule into our local array
    if (selectedScheduleIndex !== null && editingSchedule) {
      schedulesToValidate[selectedScheduleIndex] = {
        ...editingSchedule,
        studyStartDay: currentStudyStartDay,
        hasAssessmentWindow: !!editingSchedule.hasAssessmentWindow,
        assessmentWindows: editingSchedule.hasAssessmentWindow
          ? (editingSchedule.assessmentWindows || [])
          : []
      };
      // Update state for UI consistency
      setSchedules(schedulesToValidate);
    }

    // Validation - use schedulesToValidate, NOT the stale schedules state
    let isValid = true;
    const errors = [];
    const duplicateScheduleNames = [];
    
    // Validate study start day
    /*
    if (!isValidStartDay(currentStudyStartDay)) {
      isValid = false;
      errors.push('Study start day is required');
    }
    */
    
    // First check for duplicate schedule names across all schedules
    const nameMap = new Map();
    schedulesToValidate.forEach((schedule, idx) => {
      if (schedule.name && schedule.name.trim()) {
        const normalizedName = schedule.name.trim().toLowerCase();
        if (nameMap.has(normalizedName)) {
          // Track duplicates in a separate array
          duplicateScheduleNames.push({
            name: schedule.name, 
            indexes: [nameMap.get(normalizedName), idx]
          });
          isValid = false;
        } else {
          nameMap.set(normalizedName, idx);
        }
      }
    });
    
    // Show a single combined toast for duplicate schedule names
    if (duplicateScheduleNames.length > 0) {
      const uniqueDuplicates = new Set(duplicateScheduleNames.map(d => d.name));
      const message = Array.from(uniqueDuplicates).map(name => 
        `Duplicate schedule name: "${name}"`
      ).join('\n');
      toast.error(message);
    }
    
    // Validate all schedules (without duplicate name checks which we handled above)
    for (let i = 0; i < schedulesToValidate.length; i++) {
      if (!validateScheduleExceptDuplicateNames(schedulesToValidate[i], i)) {
        isValid = false;
      }
    }

    // Validate arms
    const trimmedArms = arms.map((a) => a.trim());
    if (trimmedArms.some((a) => a === '')) {
      isValid = false;
      errors.push('All arm names must be filled');
    }

    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    if (!isValid) {
      return;
    }

    try {
      // Ensure every schedule has the current study start day
      // Use schedulesToValidate to ensure we save the same data we validated
      const updatedSchedules = schedulesToValidate.map(schedule => ({
        ...schedule,
        studyStartDay: currentStudyStartDay
      }));
      
      // Clean up the data for submission
      const cleaned = updatedSchedules.map((schedule) => ({
        name: schedule.name,
        type: schedule.type,
        arms: schedule.arms,
        studyStartDay: currentStudyStartDay, // Explicitly set to current value
        dailyStartDay: schedule.dailyStartDay === '' || schedule.dailyStartDay == null
          ? null
          : parseInt(schedule.dailyStartDay, 10),
        dailyDuration: schedule.dailyDuration === '' || schedule.dailyDuration == null
          ? null
          : parseInt(schedule.dailyDuration, 10),
        standardSurveys: schedule.standardSurveys,
        respondentType: schedule.respondentType || 'participant', // FECOA-1522
        enablePushReminders: schedule.enablePushReminders === true,
        pushReminders: schedule.enablePushReminders ? schedule.pushReminders : [],
        pushReminderTimers: schedule.pushReminderTimers,
        enableEmailReminders: schedule.enableEmailReminders === true,
        emailReminders: schedule.enableEmailReminders ? schedule.emailReminders : [],
        emailReminderTimers: schedule.emailReminderTimers,
        enableMissingForms: schedule.enableMissingForms === true,
        missingForms: schedule.enableMissingForms ? schedule.missingForms : [],
        assessmentTimers: schedule.assessmentTimers,
        siteType: schedule.siteType,
        hasAssessmentWindow: schedule.hasAssessmentWindow === true,
        assessmentWindows: schedule.hasAssessmentWindow ? (schedule.assessmentWindows || []) : [],
        stages: schedule.stages.map((stage) => ({
          name: stage.name,
          startDay: parseInt(stage.startDay),
          endDay: parseInt(stage.endDay),
          frequency: {
            value: parseInt(stage.frequency.value),
            unit: stage.frequency.unit
          }
        })),
        // FECOA-1236: once-a-day persists as a single unnamed timepoint covering
        // every schedule survey; multiple-times-a-day keeps per-timepoint names
        // and survey subsets.
        timepoints: Array.isArray(schedule.timepoints)
          ? schedule.timepoints.map((tp) => ({
              id: tp.id,
              name: tp.name,
              target_time: tp.target_time,
              window_minutes: parseInt(tp.window_minutes, 10),
              // FECOA-1333: this map is a whitelist, so the flag must be listed
              // or every save silently disables enforcement.
              enforce_order: !!tp.enforce_order,
              // FECOA-1824: same whitelist rule for time gating.
              time_gated: !!tp.time_gated,
              surveys: isOnceDeliveryMode(schedule)
                ? (schedule.standardSurveys || []).map(String)
                : (tp.surveys || []).map(String),
              reminders: tp.reminders || {},
            }))
          : []
      }));

      // Save
      await studyBuilderService.updateStudyDesign(studyName, {
        // arms are top-level; siteType is per-schedule
        arms: trimmedArms,
        studyStartDay: currentStudyStartDay,
        schedules: cleaned
      });

      setShowValidation(false);
      toast.success('Study design saved successfully');
      // "Save & Submit for Approval" passes afterSave to chain the submit modal
      // instead of leaving the builder (FECOA-1600).
      if (afterSave) {
        afterSave();
      } else {
        navigate(`/studybuilder/${encodeURIComponent(studyName)}`);
      }
      return true;
    } catch (error) {
      console.error('Error saving design:', error);
      const errMsg = error.response?.data?.detail || error.message || 'Failed to save';
      toast.error(errMsg);
      return false;
    }
  };

  // Save the draft, then open the Submit-for-Approval modal (FECOA-1600/1601).
  const handleSaveAndSubmit = async () => {
    setShowSaveMenu(false);
    await handleSubmit(null, { afterSave: () => setSubmitModalOpen(true) });
  };

  // Withdraw an in-review submission back to draft so it can be edited again
  // (FECOA-1602). Unlocks the builder in place on success.
  const handleWithdraw = async () => {
    if (!draftId) return;
    setWithdrawing(true);
    try {
      await withdrawStudyDraft(draftId);
      toast.success('Submission withdrawn. You can edit the design again.');
      setReviewLocked(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not withdraw the submission.');
    } finally {
      setWithdrawing(false);
    }
  };

  // Step forms
  const renderArmsForm = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900">Define Study Arms</h3>
        <button
          onClick={handleAddArm}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Arm
        </button>
      </div>

      <div className="mb-4 text-sm text-gray-700 dark:text-gray-200">
        Fields marked with <span className="text-red-500">*</span> are required
      </div>

      <div className="space-y-4">
        {arms.map((arm, i) => (
          <div key={i} className="flex items-center space-x-4">
            <div className="flex-grow">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Arm Name
                <RequiredFieldMarker />
              </label>
              <input
                type="text"
                value={arm}
                onChange={(e) => handleArmChange(i, e.target.value)}
                placeholder="Enter arm name"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  showValidation && arm.trim() === '' ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {showValidation && arm.trim() === '' && (
                <p className="mt-1 text-sm text-red-500">Arm name is required</p>
              )}
            </div>
            {arms.length > 1 && (
              <button
                onClick={() => handleRemoveArm(i)}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors mt-6"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );

  const renderSchedulesForm = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-8"
    >
      <div className="mb-4 text-sm text-gray-700 dark:text-gray-200">
        Fields marked with <span className="text-red-500">*</span> are required
      </div>

      {/* Study Start Day */}
      <div className="mb-6">
        <Tooltip content="The reference day for your entire study timeline, expressed as an offset from each participant's enrollment date. 0 = the participant's enrollment day. Negative values are allowed for pre-study screening windows.">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Study Start Day
            <RequiredFieldMarker />
          </label>
        </Tooltip>
        <SafeInput
          type="text"
          inputMode="numeric"
          value={studyStartDay}
          onChange={(e) => handleStudyStartDayChange(e.target.value)}
          onBlur={handleStudyStartDayBlur}
          placeholder="Enter study start day"
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            showValidation && (studyStartDay === '' || studyStartDay === undefined || studyStartDay === null)
              ? 'border-red-500'
              : 'border-gray-300 dark:border-gray-600'
          }`}
        />

        {/* Live helper text + date calculator toggle */}
        {(() => {
          const desc = describeStudyStartDay(studyStartDay, todayIsoForCalc);
          return (
            <div className="mt-2 flex flex-wrap items-start justify-between gap-2 text-sm">
              <div className="text-gray-500 dark:text-gray-400 leading-snug">
                {desc ? (
                  <>
                    <span className="font-medium text-gray-700 dark:text-gray-200">{desc.relative}</span>{' '}
                    <span>{desc.projection}</span>
                  </>
                ) : (
                  <span className="italic">Enter a number (or use the date calculator) to see when the study would start.</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowStartDayCalc((s) => !s)}
                className="text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap shrink-0"
              >
                {showStartDayCalc ? 'Hide date calculator' : 'Pick a date instead'}
              </button>
            </div>
          );
        })()}

        {showStartDayCalc && (() => {
          const computedOffset = daysBetweenIso(calcRefEnrollment, calcTargetStart);
          const offsetLabel = computedOffset === 0
            ? '0 (same day)'
            : computedOffset > 0
              ? `${computedOffset} day${computedOffset === 1 ? '' : 's'} after enrollment`
              : `${Math.abs(computedOffset)} day${Math.abs(computedOffset) === 1 ? '' : 's'} before enrollment`;
          return (
            <div className="mt-3 border border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-900/10 rounded-lg p-3">
              <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                Compute the offset from two dates. The result fills in the Study Start Day field above.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Sample enrollment date
                  </label>
                  <input
                    type="date"
                    value={calcRefEnrollment}
                    onChange={(e) => setCalcRefEnrollment(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Target study start date
                  </label>
                  <input
                    type="date"
                    value={calcTargetStart}
                    onChange={(e) => setCalcTargetStart(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Computed offset</p>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-100">
                    Day {computedOffset}
                  </p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">{offsetLabel}</p>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowStartDayCalc(false)}
                  className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const newValue = String(computedOffset);
                    handleStudyStartDayChange(newValue);
                    // Sync schedules with the new value directly — calling
                    // handleStudyStartDayBlur would read stale closure state.
                    setSchedules(schedules.map((s) => ({ ...s, studyStartDay: newValue })));
                    if (selectedScheduleIndex !== null) {
                      setEditingSchedule({ ...editingSchedule, studyStartDay: newValue });
                    }
                    setShowStartDayCalc(false);
                  }}
                  className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                >
                  Use this offset
                </button>
              </div>
            </div>
          );
        })()}

        {showValidation && !isValidStartDay(studyStartDay) && (
          <p className="mt-1 text-sm text-red-500">
            Study start day is required
          </p>
        )}
      </div>

      <div className="space-y-4">
        {schedules.map((sch, idx) => (
          <div
            key={idx}
            className="flex items-center space-x-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20"
          >
            <div className="flex-grow">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Schedule Name
                <RequiredFieldMarker />
              </label>
              <input
                type="text"
                value={sch.name}
                onChange={(e) => {
                  const newName = e.target.value;
                  // Always update the name on change, but only validate on blur
                  const updated = [...schedules];
                  updated[idx] = { ...sch, name: newName };
                  setSchedules(updated);
                  
                  // If this is the currently selected schedule, update editingSchedule too
                  if (idx === selectedScheduleIndex) {
                    setEditingSchedule(prev => ({
                      ...prev,
                      name: newName
                    }));
                  }
                }}
                onBlur={(e) => {
                  const newName = e.target.value;
                  // Only validate duplicate names when the user finishes typing
                  if (newName.trim() && isDuplicateName(schedules, newName, idx)) {
                    toast.error('A schedule with this name already exists');
                  }
                }}  
                placeholder="Enter schedule name"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  showValidation && sch.name.trim() === '' ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {showValidation && sch.name.trim() === '' && (
                <p className="mt-1 text-sm text-red-500">
                  Schedule name is required
                </p>
              )}
            </div>
            <button
              onClick={() => handleScheduleSelect(idx)}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                selectedScheduleIndex === idx
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Configure
            </button>
            <button
              onClick={() => handleRemoveSchedule(idx)}
              className="p-2 text-gray-400 hover:text-red-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ))}
        <button
          onClick={handleAddSchedule}
          className="flex items-center space-x-2 text-primary-600 hover:text-primary-700"
        >
          <Plus className="w-5 h-5" />
          <span>Add Schedule</span>
        </button>
      </div>

      {selectedScheduleIndex !== null && editingSchedule && (
        <div className="mt-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900/20">
          <div className="flex items-start justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Configure {editingSchedule.name}
            </h3>
            {isLiveScheduleId(editingSchedule.id) && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowVersionHistory(true)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Version history
                </button>
                <button
                  type="button"
                  onClick={() => setShowAmendModal(true)}
                  className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700"
                >
                  Amend live schedule
                </button>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* 1. Schedule Type & Assessment Timers */}
            <div className="mb-4">
              <Tooltip content="Determines how participants complete assessments. Daily Assessment: Fixed daily schedule. Patient Prompted: Participants can start when ready. Timer types: Include countdown timers to encourage completion within set timeframes.">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Schedule Type
                  <RequiredFieldMarker />
                </label>
              </Tooltip>
              <SafeSelect
                value={editingSchedule.type}
                onChange={(e) => handleScheduleChange('type', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  showValidation && !editingSchedule.type ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <option value="">Select type</option>
                {scheduleTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </SafeSelect>
              {showValidation && !editingSchedule.type && (
                <p className="mt-1 text-sm text-red-500">
                  Schedule type is required
                </p>
              )}
            </div>

            {/* Assessment Timers Section — only the "- Timer" types (FECOA-1625) */}
            {isTimerType(editingSchedule.type) && (editingSchedule.assessmentTimers || []).length === 0 && (
              <div className="mb-4">
                <Tooltip content="Add timers to create time limits for participant assessments. Timers help maintain study protocol consistency and can improve data quality by encouraging focused responses.">
                  <button
                    onClick={handleAddAssessmentTimer}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Enable Timer
                  </button>
                </Tooltip>
              </div>
            )}
            {isTimerType(editingSchedule.type) && (editingSchedule.assessmentTimers || []).length > 0 && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <Tooltip content="Add timers to create time limits for participant assessments. Timers help maintain study protocol consistency and can improve data quality by encouraging focused responses.">
                    <h4 className="text-lg font-medium text-gray-900">
                      Assessment Timers
                    </h4>
                  </Tooltip>
                  <button
                    onClick={handleAddAssessmentTimer}
                    className="inline-flex items-center px-2 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Timer
                  </button>
                </div>
                <div className="space-y-4">
                  {(editingSchedule.assessmentTimers || []).map((timer, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
                    >
                      <div className="flex-grow space-y-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={timer.enabled}
                            onChange={(e) =>
                              handleUpdateAssessmentTimer(
                                index,
                                'enabled',
                                e.target.checked
                              )
                            }
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <Tooltip content="When enabled, this timer will be active for participant assessments. Participants will see a countdown indicating remaining time to complete the assessment.">
                            <label className="ml-2 text-sm text-gray-700 dark:text-gray-200">
                              Enable Timer
                            </label>
                          </Tooltip>
                        </div>
                        <div>
                          <Tooltip content="How much time participants have to complete the assessment once they begin. The timer creates urgency while allowing enough time for thoughtful responses.">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                              Minutes
                            </label>
                          </Tooltip>
                          <SafeSelect
                            value={timer.minutes}
                            onChange={(e) =>
                              handleUpdateAssessmentTimer(
                                index,
                                'minutes',
                                parseInt(e.target.value)
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">Select minutes</option>
                            {[5, 10, 15, 20, 30, 45, 60, 90, 120].map((min) => (
                              <option key={min} value={min}>
                                {min} minutes
                              </option>
                            ))}
                          </SafeSelect>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={timer.addAlarm}
                            onChange={(e) =>
                              handleUpdateAssessmentTimer(
                                index,
                                'addAlarm',
                                e.target.checked
                              )
                            }
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <Tooltip content="When enabled, participants will receive an additional audible or visual alert as the timer approaches expiration, providing extra encouragement to complete the assessment.">
                            <label className="ml-2 text-sm text-gray-700 dark:text-gray-200">
                              Add Alarm
                            </label>
                          </Tooltip>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveAssessmentTimer(index)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Survey Selection */}
            <div>
              <Tooltip content="Select the assessments that participants will complete during this schedule. If you select multiple surveys, they will be linked and presented to participants in sequence during each assessment session.">
                <div>
                  <MultiSelectDropdown
                    label="Survey"
                    // FECOA-1676: list every published survey regardless of COA
                    // type. Respondent type ("Who should answer this") routes the
                    // schedule to a user; it no longer restricts which surveys can
                    // be delivered, so an ObsRO survey is selectable on a participant
                    // schedule and vice versa.
                    options={surveys.map((sv) => ({
                        value: sv.id.toString(),
                        label: sv.respondent_type === 'caregiver' ? `${sv.name} (ObsRO)` : sv.name
                      }))}
                    selectedValues={editingSchedule.standardSurveys}
                    onChange={(vals) => {
                      console.log(`[ScheduleBuilder] Surveys selected for schedule "${editingSchedule.name}":`, vals);
                      console.log(`[ScheduleBuilder] Previous survey selection:`, editingSchedule.standardSurveys);
                      handleScheduleChange('standardSurveys', vals);
                      // Update linked status whenever surveys are added/removed from a schedule
                      // is_linked will be determined automatically based on number of surveys
                      surveyService.updateSurveysLinkedStatus(vals)
                        .then(response => {
                          console.log(`[ScheduleBuilder] Successfully updated survey linked status for ${vals.length} surveys.`);
                        })
                        .catch(error => {
                          console.error(`[ScheduleBuilder] Error updating survey linked status:`, error);
                        });
                    }}
                    required={true}
                    showValidation={showValidation}
                    resolvingLabels={surveysLoading}
                  />
                </div>
              </Tooltip>
              <SurveyOrderingComponent
                selectedSurveys={editingSchedule.standardSurveys}
                allSurveys={surveys}
                onChange={(newOrder) => handleScheduleChange('standardSurveys', newOrder)}
              />

              {/* FECOA-1522: schedule-level Respondent Type. It filters the survey
                  picker above (to assessments tagged with the matching type) and
                  routes the schedule to the right user. Replaces the old
                  per-survey global setter. */}
              <div className="mt-4">
                <Tooltip content="Determines who completes assessments in this schedule. Caregiver schedules are only assigned to linked caregivers when Caregiver Support is enabled on the study.">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
                    Who should answer this
                  </label>
                </Tooltip>
                <select
                  value={editingSchedule.respondentType || 'participant'}
                  onChange={(e) => {
                    const rt = e.target.value;
                    // FECOA-1676: surveys are answerable regardless of COA type, so
                    // changing who answers no longer invalidates the selected
                    // surveys — keep the selection instead of resetting it.
                    handleScheduleChange('respondentType', rt);
                  }}
                  className="w-full py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800"
                >
                  <option value="participant">Participant</option>
                  {/* FECOA-1624: hide Caregiver when support is disabled, unless this
                      schedule was already saved as caregiver (keep its value visible). */}
                  {(allowCaregivers || editingSchedule.respondentType === 'caregiver') && (
                    <option value="caregiver">Caregiver</option>
                  )}
                  <option value="clinician">Clinician</option>
                </select>
                {editingSchedule.respondentType === 'caregiver' && (
                  allowCaregivers ? (
                    // FECOA-1814: support is already on in this branch, so don't tell
                    // the user to go enable it. Informational, not an error.
                    <div className="mt-2 p-3 rounded-md bg-blue-50/40 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900 text-sm text-gray-600 dark:text-gray-300">
                      This schedule will only be assigned to caregivers who are linked
                      to a participant.
                    </div>
                  ) : (
                    <div className="mt-2 p-3 rounded-md bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-sm text-rose-700 dark:text-rose-300">
                      <strong>Caregiver Support is disabled</strong> for this study, so this
                      schedule will not be assigned until it is re-enabled in Study Details.
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Site Type (FECOA-1658: Where comes before When/Delivery so the
                onsite + timepoints warning can fire in context). */}
            <div className="mb-4">
              <Tooltip content="Determines where participants complete assessments. On-site: Participants visit a physical location. Remote: Participants complete assessments from their own location using the mobile app or web portal.">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Site Type
                  <RequiredFieldMarker />
                </label>
              </Tooltip>
              <SafeSelect
                value={editingSchedule.siteType}
                onChange={(e) => handleScheduleChange('siteType', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  showValidation && !editingSchedule.siteType
                    ? 'border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <option value="">Select a type</option>
                <option value="on-site">On-site</option>
                <option value="remote">Remote</option>
              </SafeSelect>
              {showValidation && !editingSchedule.siteType && (
                <p className="mt-1 text-sm text-red-500">
                  Site type is required
                </p>
              )}
            </div>

            {/* FECOA-1236: Consolidated Delivery Window — always visible (no enable
                checkbox). The Open/Close Day range applies to every schedule type;
                the intra-day cadence (Once vs Multiple timepoints) is shown only for
                Scheduled / Daily Assessment. Patient Prompted types are ad-hoc, so
                they get the day range only. */}
            {editingSchedule.type && (
              <div className="mb-4 mt-6 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    Delivery Window
                  </h4>
                </div>

                {/* Assessment Window Days — open/close day range, all schedule types */}
                <div>
                  <Tooltip content="The range of study days this assessment is open. Applies to the whole delivery window; for clinic visits this is the participant arrival window (e.g. 3 days early / 3 days late).">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Assessment Window Days
                    </label>
                  </Tooltip>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Open Window Day
                        <RequiredFieldMarker />
                      </label>
                      <SafeSelect
                        value={editingSchedule.assessmentWindows?.[0]?.openWindowDay ?? 0}
                        onChange={(e) => updateAssessmentWindow(0, 'openWindowDay', parseInt(e.target.value, 10))}
                        className="block w-full py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-800"
                      >
                        <option value={0}>Expected Day</option>
                        {Array.from({ length: 7 }, (_, i) => -(i + 1)).map((day) => (
                          <option key={day} value={day}>
                            {day} Day{Math.abs(day) !== 1 ? 's' : ''} before Expected Day
                          </option>
                        ))}
                      </SafeSelect>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Close Window Day
                        <RequiredFieldMarker />
                      </label>
                      <SafeSelect
                        value={editingSchedule.assessmentWindows?.[0]?.closeWindowDay ?? 0}
                        onChange={(e) => updateAssessmentWindow(0, 'closeWindowDay', parseInt(e.target.value, 10))}
                        className="block w-full py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-800"
                      >
                        <option value={0}>Expected Day</option>
                        {Array.from({ length: 7 }, (_, i) => i + 1).map((day) => (
                          <option key={day} value={day}>
                            +{day} Day{day !== 1 ? 's' : ''} after Expected Day
                          </option>
                        ))}
                      </SafeSelect>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    These day boundaries apply to the whole delivery window for this schedule.
                  </p>
                </div>

                {/* Intra-day cadence — Scheduled / Daily Assessment only */}
                {supportsTimepoints(editingSchedule.type) ? (
                  <div>
                    <Tooltip content="Once a day = one window per scheduled day. Multiple times a day = named timepoints (e.g. Morning, Afternoon) with their own start times and durations, each delivering a chosen subset of surveys.">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                        Delivery
                      </label>
                    </Tooltip>
                    <div className="flex items-center gap-6 mb-3">
                      <label className="inline-flex items-center text-sm text-gray-700 dark:text-gray-200">
                        <input
                          type="radio"
                          className="mr-2 text-primary-600 focus:ring-primary-500"
                          checked={isOnceDeliveryMode(editingSchedule)}
                          onChange={() => handleSetDeliveryMode('once')}
                        />
                        Once a day
                      </label>
                      <label className="inline-flex items-center text-sm text-gray-700 dark:text-gray-200">
                        <input
                          type="radio"
                          className="mr-2 text-primary-600 focus:ring-primary-500"
                          checked={!isOnceDeliveryMode(editingSchedule)}
                          onChange={() => handleSetDeliveryMode('multiple')}
                        />
                        Multiple times a day (timepoints)
                      </label>
                    </div>

                    {/* FECOA-1660: warn (don't block) when an on-site visit is paired
                        with intra-day timepoints. */}
                    {isOnsiteTimepointConflict(editingSchedule) && (
                      <OnsiteTimepointWarning
                        className="mb-3"
                        text="Onsite studies typically use a single daily window. Multiple timepoints are supported but verify your site protocol before proceeding."
                      />
                    )}

                    {/* Once a day: one window (target time + length), no name; delivers
                        all schedule surveys. */}
                    {isOnceDeliveryMode(editingSchedule) && (() => {
                      const onceTp = (editingSchedule.timepoints || [])[0] || { target_time: '00:00', window_minutes: 1440 };
                      const badTime = onceTp.target_time && !HHMM_RE.test(onceTp.target_time);
                      const badMinutes = !onceTp.window_minutes || onceTp.window_minutes <= 0 || onceTp.window_minutes > 1440;
                      return (
                        <div className="grid grid-cols-2 gap-3 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                              Target time (HH:MM, 24h)
                              <RequiredFieldMarker />
                            </label>
                            <input
                              type="time"
                              value={onceTp.target_time}
                              onChange={(e) => handleOnceWindowChange('target_time', e.target.value)}
                              className={`w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                showValidation && badTime ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                              }`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                              Window length (minutes)
                              <RequiredFieldMarker />
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="1440"
                              step="1"
                              value={onceTp.window_minutes ?? ''}
                              onChange={(e) =>
                                handleOnceWindowChange('window_minutes', e.target.value === '' ? '' : parseInt(e.target.value, 10))
                              }
                              className={`w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                showValidation && badMinutes ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                              }`}
                            />
                          </div>
                          {/* FECOA-1333: strict completion order applies to the
                              once-a-day window too; without a control here the
                              flag would be invisible-but-saved state. */}
                          <div className="col-span-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-200">
                              <input
                                type="checkbox"
                                className="mr-2 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                                checked={!!onceTp.enforce_order}
                                onChange={(e) => handleOnceWindowChange('enforce_order', e.target.checked)}
                              />
                              Enforce completion order
                            </label>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Participants must complete this schedule's surveys in the order shown in the survey list before the next one unlocks.
                            </p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Multiple times a day: named timepoint rows. */}
                    {!isOnceDeliveryMode(editingSchedule) && (
                  <div className="space-y-3 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                    {editingSchedule.timepoints.map((tp, tpIdx) => {
                      const isBadTime = tp.target_time && !HHMM_RE.test(tp.target_time);
                      const isBadMinutes = !tp.window_minutes || tp.window_minutes <= 0 || tp.window_minutes > 1440;
                      const noSurveys = !tp.surveys || tp.surveys.length === 0;
                      const badName = !tp.name || !tp.name.trim();
                      // FECOA-1676: a timepoint can still reference a survey that is no
                      // longer in the schedule's survey list. Flag it so the offending
                      // section is highlighted, not just described in a toast.
                      const scheduleSurveyIds = new Set((editingSchedule.standardSurveys || []).map(String));
                      const isBadSurveys = (tp.surveys || []).some((s) => !scheduleSurveyIds.has(String(s)));
                      const tpHasError = showValidation && (isBadTime || isBadMinutes || noSurveys || badName || isBadSurveys);
                      return (
                        <div
                          key={tp.id || tpIdx}
                          className={`bg-white dark:bg-gray-800 border rounded-lg p-4 ${
                            tpHasError
                              ? 'border-red-500 ring-1 ring-red-500'
                              : 'border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex-1">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                Name
                                <RequiredFieldMarker />
                              </label>
                              <input
                                type="text"
                                value={tp.name}
                                onChange={(e) => handleTimepointChange(tpIdx, 'name', e.target.value)}
                                placeholder="e.g. Morning Dose"
                                className={`w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                  showValidation && !tp.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                }`}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveTimepoint(tpIdx)}
                              disabled={(editingSchedule.timepoints || []).length <= 1}
                              className="mt-5 p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-400"
                              aria-label="Remove timepoint"
                              title={
                                (editingSchedule.timepoints || []).length <= 1
                                  ? 'At least one timepoint is required'
                                  : 'Remove timepoint'
                              }
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                Target time (HH:MM, 24h)
                                <RequiredFieldMarker />
                              </label>
                              <input
                                type="time"
                                value={tp.target_time}
                                onChange={(e) => handleTimepointChange(tpIdx, 'target_time', e.target.value)}
                                className={`w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                  showValidation && isBadTime ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                }`}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                                Window length (minutes)
                                <RequiredFieldMarker />
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="1440"
                                step="1"
                                value={tp.window_minutes ?? ''}
                                onChange={(e) =>
                                  handleTimepointChange(
                                    tpIdx,
                                    'window_minutes',
                                    e.target.value === '' ? '' : parseInt(e.target.value, 10),
                                  )
                                }
                                className={`w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                  showValidation && isBadMinutes ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                                }`}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                              Surveys delivered at this timepoint
                              <RequiredFieldMarker />
                            </label>
                            <div className="space-y-1">
                              {(editingSchedule.standardSurveys || []).length === 0 ? (
                                <p className="text-xs italic text-gray-500 dark:text-gray-400">
                                  Add surveys to the schedule above first.
                                </p>
                              ) : (
                                <>
                                  {(() => {
                                    const allIds = editingSchedule.standardSurveys.map(String);
                                    const selected = (tp.surveys || []).map(String);
                                    const allChecked = allIds.every((id) => selected.includes(id));
                                    return (
                                      <label className="flex items-center text-sm font-medium italic text-gray-600 dark:text-gray-300 pb-1 mb-1 border-b border-gray-200 dark:border-gray-700">
                                        <input
                                          type="checkbox"
                                          className="mr-2 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                                          checked={allChecked}
                                          ref={(el) => {
                                            if (el) {
                                              el.indeterminate = !allChecked && selected.length > 0;
                                            }
                                          }}
                                          onChange={(e) =>
                                            handleTimepointChange(
                                              tpIdx,
                                              'surveys',
                                              e.target.checked ? allIds : [],
                                            )
                                          }
                                        />
                                        All surveys
                                      </label>
                                    );
                                  })()}
                                  {editingSchedule.standardSurveys.map((surveyId) => {
                                    const sv = surveys.find((s) => s.id.toString() === surveyId.toString());
                                    const checked = (tp.surveys || []).map(String).includes(String(surveyId));
                                    return (
                                    <label key={surveyId} className="flex items-center text-sm text-gray-700 dark:text-gray-200">
                                      <input
                                        type="checkbox"
                                        className="mr-2 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                                        checked={checked}
                                        onChange={(e) => {
                                          const current = (tp.surveys || []).map(String);
                                          const next = e.target.checked
                                            ? [...current, String(surveyId)]
                                            : current.filter((s) => s !== String(surveyId));
                                          handleTimepointChange(tpIdx, 'surveys', next);
                                        }}
                                      />
                                      {sv
                                        ? (sv.respondent_type === 'caregiver' ? `${sv.name} (ObsRO)` : sv.name)
                                        : surveysLoading
                                          ? <span className="inline-block w-24 h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" role="status" aria-label="Loading survey name" />
                                          : surveyId}
                                    </label>
                                    );
                                  })}
                                </>
                              )}
                            </div>
                            {showValidation && noSurveys && (
                              <p className="mt-1 text-xs text-red-500">Pick at least one survey</p>
                            )}
                            {showValidation && !noSurveys && isBadSurveys && (
                              <p className="mt-1 text-xs text-red-500">
                                A selected survey is no longer in this schedule's survey list above. Uncheck it here, or add it back to the schedule's surveys.
                              </p>
                            )}
                          </div>
                          {/* FECOA-1333: strict within-timepoint completion order. */}
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-200">
                              <input
                                type="checkbox"
                                className="mr-2 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                                checked={!!tp.enforce_order}
                                onChange={(e) =>
                                  handleTimepointChange(tpIdx, 'enforce_order', e.target.checked)
                                }
                              />
                              Enforce completion order
                            </label>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Participants must complete these surveys in the order shown below before the next one unlocks.
                            </p>
                            {tp.enforce_order && (tp.surveys || []).length > 0 && (
                              <ol className="mt-2 space-y-1">
                                {(tp.surveys || []).map((surveyId, sIdx) => {
                                  const sv = surveys.find((s) => s.id.toString() === String(surveyId));
                                  const ordered = (tp.surveys || []).map(String);
                                  const move = (from, to) => {
                                    if (to < 0 || to >= ordered.length) return;
                                    const next = [...ordered];
                                    const [moved] = next.splice(from, 1);
                                    next.splice(to, 0, moved);
                                    handleTimepointChange(tpIdx, 'surveys', next);
                                  };
                                  return (
                                    <li
                                      key={surveyId}
                                      className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-800 rounded px-2 py-1"
                                    >
                                      <span>
                                        <span className="font-medium mr-2">{sIdx + 1}.</span>
                                        {sv
                                        ? (sv.respondent_type === 'caregiver' ? `${sv.name} (ObsRO)` : sv.name)
                                        : surveysLoading
                                          ? <span className="inline-block w-24 h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" role="status" aria-label="Loading survey name" />
                                          : surveyId}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <button
                                          type="button"
                                          disabled={sIdx === 0}
                                          onClick={() => move(sIdx, sIdx - 1)}
                                          className="px-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30"
                                          aria-label="Move up"
                                        >
                                          ↑
                                        </button>
                                        <button
                                          type="button"
                                          disabled={sIdx === (tp.surveys || []).length - 1}
                                          onClick={() => move(sIdx, sIdx + 1)}
                                          className="px-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30"
                                          aria-label="Move down"
                                        >
                                          ↓
                                        </button>
                                      </span>
                                    </li>
                                  );
                                })}
                              </ol>
                            )}
                          </div>
                          {/* FECOA-1824: strict daily time gating. */}
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-200">
                              <input
                                type="checkbox"
                                className="mr-2 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                                checked={!!tp.time_gated}
                                onChange={(e) =>
                                  handleTimepointChange(tpIdx, 'time_gated', e.target.checked)
                                }
                              />
                              Time-gate delivery
                            </label>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Surveys are only available during this timepoint's daily slot (target time until the window length elapses). The schedule's Open/Close Day range is ignored, and a slot that passes uncompleted is marked missed for that day.
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={handleAddTimepoint}
                      className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add timepoint</span>
                    </button>
                  </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs italic text-gray-500 dark:text-gray-400">
                    Patient Prompted schedules are ad-hoc: the participant starts the assessment any time within the Open/Close Day range above. Fixed intra-day timepoints don't apply.
                  </p>
                )}
              </div>
            )}

            {/* 2. Arms */}
            <div>
              <Tooltip content="Study arms represent different treatment groups or conditions in your study. Participants are assigned to specific arms, and each schedule can be applied to one or more arms depending on your study design.">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Select Arms
                  <RequiredFieldMarker />
                </label>
              </Tooltip>
              <div className="space-y-2">
                {arms.map((arm, i) => (
                  <div key={i} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`arm-${i}`}
                      checked={editingSchedule.arms.includes(arm)}
                      onChange={(e) => {
                        let newArms;
                        if (e.target.checked) {
                          newArms = [...editingSchedule.arms, arm];
                        } else {
                          newArms = editingSchedule.arms.filter((a) => a !== arm);
                        }
                        handleScheduleChange('arms', newArms);
                      }}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor={`arm-${i}`} className="ml-2 text-gray-700 dark:text-gray-200">
                      {arm}
                    </label>
                  </div>
                ))}
              </div>
              {showValidation && editingSchedule.arms.length === 0 && (
                <p className="mt-1 text-sm text-red-500">
                  Please select at least one arm
                </p>
              )}
            </div>

            {/* 3. Stages */}
            {isStagesDisabled ? (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <Tooltip content="Multi-stage configuration is not applicable when daily cadence is implied by the schedule type. Use Start Day + Schedule Duration below.">
                    <h4 className="text-lg font-medium text-gray-900 opacity-50">
                      Stages
                    </h4>
                  </Tooltip>
                  <span className="text-sm text-gray-400 italic">Daily cadence enforced</span>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Tooltip content="The first study day on which this daily assessment should be delivered. Defaults to the Study Start Day but can be later if participants should not receive this assessment until a specific day.">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                          Start Day
                          <RequiredFieldMarker />
                        </label>
                      </Tooltip>
                      <SafeInput
                        type="number"
                        min={studyStartDay || '0'}
                        step="1"
                        value={editingSchedule.dailyStartDay ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          const updated = { ...editingSchedule };
                          updated.dailyStartDay = val;
                          if (val !== '' && updated.dailyDuration) {
                            const startDay = parseInt(val, 10);
                            const endDay = String(startDay + parseInt(updated.dailyDuration, 10) - 1);
                            updated.stages = [{
                              name: 'Daily',
                              startDay: String(startDay),
                              endDay,
                              frequency: { value: '1', unit: 'days' }
                            }];
                          } else {
                            updated.stages = [{
                              name: 'Daily',
                              startDay: val,
                              endDay: '',
                              frequency: { value: '1', unit: 'days' }
                            }];
                          }
                          setEditingSchedule(updated);
                          const newSchedules = [...schedules];
                          newSchedules[selectedScheduleIndex] = updated;
                          setSchedules(newSchedules);
                        }}
                        onWheel={(e) => e.target.blur()}
                        placeholder={`e.g. ${studyStartDay || '1'}`}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          showValidation && (editingSchedule.dailyStartDay === '' || editingSchedule.dailyStartDay == null || parseInt(editingSchedule.dailyStartDay, 10) < parseInt(studyStartDay || '0', 10))
                            ? 'border-red-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                      {showValidation && (editingSchedule.dailyStartDay === '' || editingSchedule.dailyStartDay == null) && (
                        <p className="mt-1 text-sm text-red-500">
                          Start day is required
                        </p>
                      )}
                      {showValidation && editingSchedule.dailyStartDay !== '' && editingSchedule.dailyStartDay != null && parseInt(editingSchedule.dailyStartDay, 10) < parseInt(studyStartDay || '0', 10) && (
                        <p className="mt-1 text-sm text-red-500">
                          Start day must be on or after the study start day ({studyStartDay || '0'})
                        </p>
                      )}
                    </div>
                    <div>
                      <Tooltip content="How many days the daily assessment should run, starting from the Start Day above. For example, entering 30 means participants will receive daily assessments for 30 days.">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                          Schedule Duration (days)
                          <RequiredFieldMarker />
                        </label>
                      </Tooltip>
                      <SafeInput
                        type="number"
                        min="1"
                        step="1"
                        value={editingSchedule.dailyDuration || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          const startDay = parseInt(editingSchedule.dailyStartDay || studyStartDay || '1', 10);
                          const endDay = val ? String(startDay + parseInt(val, 10) - 1) : '';
                          const updated = { ...editingSchedule };
                          updated.dailyDuration = val;
                          updated.stages = [{
                            name: 'Daily',
                            startDay: String(startDay),
                            endDay,
                            frequency: { value: '1', unit: 'days' }
                          }];
                          setEditingSchedule(updated);
                          const newSchedules = [...schedules];
                          newSchedules[selectedScheduleIndex] = updated;
                          setSchedules(newSchedules);
                        }}
                        onWheel={(e) => e.target.blur()}
                        placeholder="e.g. 30"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          showValidation && !editingSchedule.dailyDuration
                            ? 'border-red-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                      {showValidation && !editingSchedule.dailyDuration && (
                        <p className="mt-1 text-sm text-red-500">
                          Schedule duration is required
                        </p>
                      )}
                    </div>
                  </div>
                  {editingSchedule.dailyDuration && editingSchedule.dailyStartDay !== '' && editingSchedule.dailyStartDay != null && (
                    <p className="mt-3 text-sm text-gray-500">
                      Daily assessments from Day {editingSchedule.dailyStartDay} to Day {parseInt(editingSchedule.dailyStartDay, 10) + parseInt(editingSchedule.dailyDuration, 10) - 1}
                    </p>
                  )}
                </div>
              </div>
            ) : (
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <Tooltip content="Stages divide your study timeline into distinct phases. Each stage defines when assessments occur and how frequently. For example, you might have a 'Baseline' stage (Days 1-7, daily assessments) followed by a 'Treatment' stage (Days 8-28, twice weekly assessments).">
                  <h4 className="text-lg font-medium text-gray-900">
                    Stages
                    <RequiredFieldMarker />
                  </h4>
                </Tooltip>
                <button
                  onClick={() => {
                    const st = [...editingSchedule.stages];
                    st.push({
                      name: '',
                      startDay: '',
                      endDay: '',
                      frequency: { value: '', unit: 'days' }
                    });
                    handleScheduleChange('stages', st);
                  }}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Stage
                </button>
              </div>

              {editingSchedule.stages.map((stage, i) => (
                <div key={i} className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-4">
                  <div className="flex justify-between items-center">
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Stage {i + 1}
                    </h5>
                    {editingSchedule.stages.length > 1 && !isStagesDisabled && (
                      <button
                        onClick={() => {
                          const st = [...editingSchedule.stages];
                          st.splice(i, 1);
                          handleScheduleChange('stages', st);
                        }}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* FECOA-1660 AC-1b: surface the onsite + timepoints warning
                      inside each stage, not just under the delivery mode radios. */}
                  {isOnsiteTimepointConflict(editingSchedule) && (
                    <OnsiteTimepointWarning
                      text="Onsite studies typically use a single daily window. Multiple timepoints are supported but verify your site protocol before proceeding."
                    />
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Tooltip content="A descriptive name for this phase of your study. Stages help organize your study timeline into meaningful periods (e.g., 'Baseline', 'Treatment Phase', 'Follow-up').">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                          Stage Name
                          <RequiredFieldMarker />
                        </label>
                      </Tooltip>
                      <input
                        type="text"
                        value={stage.name}
                        onChange={(e) => handleStageChange(i, 'name', e.target.value)}
                        disabled={isStagesDisabled}
                        placeholder="Stage name"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          showValidation && !isStagesDisabled && !stage.name.trim() ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        } ${isStagesDisabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
                      />
                      {showValidation && !isStagesDisabled && !stage.name.trim() && (
                        <p className="mt-1 text-sm text-red-500">
                          Stage name is required
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Tooltip content="The day when this stage begins, relative to your Study Start Day. For example, if Study Start Day is 1 and Stage Start Day is 8, this stage will begin 7 days after participant enrollment.">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                            Start Day
                            <RequiredFieldMarker />
                          </label>
                        </Tooltip>
                        <SafeInput
                          type="number"
                          min="0"
                          step="1"
                          value={stage.startDay}
                          onChange={(e) =>
                            handleStageChange(i, 'startDay', e.target.value)
                          }
                          disabled={isStagesDisabled}
                          onWheel={(e) => e.target.blur()}
                          placeholder="Start day"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            showValidation && !isStagesDisabled && (stage.startDay === '' || (stage.startDay !== '' && parseInt(stage.startDay) < parseInt(studyStartDay)) || (i > 0 && stage.startDay !== '' && editingSchedule.stages[i - 1].endDay !== '' && parseInt(stage.startDay) <= parseInt(editingSchedule.stages[i - 1].endDay)))
                              ? 'border-red-500'
                              : 'border-gray-300 dark:border-gray-600'
                          } ${isStagesDisabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
                        />
                        {showValidation && !isStagesDisabled && !isValidStartDay(stage.startDay) && (
                          <p className="mt-1 text-sm text-red-500">
                            Start day is required
                          </p>
                        )}
                        {showValidation && !isStagesDisabled && stage.startDay !== '' && parseInt(stage.startDay) < parseInt(studyStartDay) && (
                          <p className="mt-1 text-sm text-red-500">
                            Start day must be greater than or equal to study start day ({studyStartDay})
                          </p>
                        )}
                        {showValidation && !isStagesDisabled && i > 0 && stage.startDay !== '' && editingSchedule.stages[i - 1].endDay !== '' && parseInt(stage.startDay) <= parseInt(editingSchedule.stages[i - 1].endDay) && (
                          <p className="mt-1 text-sm text-red-500">
                            Start day must be after Stage {i}'s end day ({editingSchedule.stages[i - 1].endDay})
                          </p>
                        )}
                      </div>
                      <div>
                        <Tooltip content="The final day when this stage ends, relative to your Study Start Day. Participants will complete assessments through this day. Must be greater than or equal to the Stage Start Day.">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                            End Day
                            <RequiredFieldMarker />
                          </label>
                        </Tooltip>
                        <SafeInput
                          type="number"
                          min="0"
                          step="1"
                          value={stage.endDay}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleStageChange(i, 'endDay', value);
                          }}
                          disabled={isStagesDisabled}
                          onWheel={(e) => e.target.blur()}
                          placeholder="End day"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            showValidation && !isStagesDisabled && (stage.endDay === '' || (stage.endDay !== '' && parseInt(stage.endDay) < parseInt(stage.startDay)))
                              ? 'border-red-500'
                              : 'border-gray-300 dark:border-gray-600'
                          } ${isStagesDisabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
                        />
                        {showValidation && !isStagesDisabled && stage.endDay === '' && (
                          <p className="mt-1 text-sm text-red-500">
                            End day is required
                          </p>
                        )}
                        {showValidation && !isStagesDisabled && stage.endDay !== '' && parseInt(stage.endDay) < parseInt(stage.startDay) && (
                          <p className="mt-1 text-sm text-red-500">
                            End day must be greater than or equal to start day
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="md:col-span-2 grid grid-cols-2 gap-2">
                      <div>
                        <Tooltip content="How often participants complete assessments during this stage. For example, '2 days' means assessments every 2 days, '1 week' means weekly assessments. Frequency must fit within the stage duration.">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                            Frequency
                            <RequiredFieldMarker />
                          </label>
                        </Tooltip>
                        <SafeInput
                          type="number"
                          min="1"
                          value={stage.frequency.value}
                          onChange={(e) => {
                            // Store the value directly as entered to prevent scroll issues
                            const value = e.target.value;
                            handleStageChange(i, 'frequencyValue', value);
                          }}
                          disabled={isStagesDisabled}
                          onWheel={(e) => e.target.blur()}
                          placeholder="Frequency"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            showValidation && !isStagesDisabled && (!stage.frequency.value || stage.frequency.value < 1)
                              ? 'border-red-500'
                              : 'border-gray-300 dark:border-gray-600'
                          } ${isStagesDisabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
                        />
                        {showValidation && !isStagesDisabled &&
                          (!stage.frequency.value || stage.frequency.value < 1) && (
                            <p className="mt-1 text-sm text-red-500">
                              Frequency must be &gt; 0
                            </p>
                          )}
                      </div>
                      <div>
                        <Tooltip content="The time unit for assessment frequency. 'Days' for daily intervals, 'weeks' for weekly intervals, 'months' for monthly intervals, or 'years' for yearly intervals.">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                            Unit
                            <RequiredFieldMarker />
                          </label>
                        </Tooltip>
                        <SafeSelect
                          value={stage.frequency.unit}
                          onChange={(e) =>
                            handleStageChange(i, 'frequencyUnit', e.target.value)
                          }
                          disabled={isStagesDisabled}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            showValidation && !isStagesDisabled && !stage.frequency.unit
                              ? 'border-red-500'
                              : 'border-gray-300 dark:border-gray-600'
                          } ${isStagesDisabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
                        >
                          {frequencyUnits.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </SafeSelect>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {showValidation && editingSchedule.stages.length === 0 && (
                <p className="mt-1 text-sm text-red-500">
                  At least one stage is required
                </p>
              )}
            </div>
            )}

            {/*  --- Section for push/email/missing forms --- */}
            <div className="mt-8 space-y-4">
              {/* Enable Push Reminders */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enablePushReminders"
                  checked={editingSchedule.enablePushReminders}
                  onChange={(e) =>
                    handleScheduleChange('enablePushReminders', e.target.checked)
                  }
                  className="h-4 w-4 text-primary-600 rounded border-gray-300 dark:border-gray-600 focus:ring-primary-500"
                />
                <Tooltip content="Send automated push notifications to participants' mobile devices to remind them about upcoming assessments. You can customize the message content and timing.">
                  <label
                    htmlFor="enablePushReminders"
                    className="text-sm font-medium text-gray-700 dark:text-gray-200"
                  >
                    Enable Push Reminders
                  </label>
                </Tooltip>
              </div>
              {editingSchedule.enablePushReminders && (
                <>
                  <MultiSelectDropdown
                    label="Select Push Reminder Template(s)"
                    options={pushReminders.map((pr) => ({
                      value: pr.id.toString(),
                      label: pr.name
                    }))}
                    selectedValues={editingSchedule.pushReminders}
                    onChange={(vals) => handleScheduleChange('pushReminders', vals)}
                    required={editingSchedule.enablePushReminders}
                    showValidation={showValidation}
                    resolvingLabels={remindersLoading}
                  />
                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <Tooltip content="Set specific times when push reminders will be sent to participants. Multiple times allow for repeated reminders throughout the day. Times are converted to UTC for consistent delivery across time zones.">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Reminder Times</label>
                      </Tooltip>
                      <button
                        type="button"
                        onClick={() => handleAddTimer('push')}
                        className="inline-flex items-center px-2 py-1 text-sm text-primary-600 hover:text-primary-800"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Time
                      </button>
                    </div>
                    {editingSchedule.pushReminderTimers.map((time, index) => (
                      <TimeInput
                        key={index}
                        label={`Time ${index + 1}`}
                        time={time}
                        onChange={(field, value) => handleTimeChange('push', index, field, value)}
                        onRemove={editingSchedule.pushReminderTimers.length > 1 ? () => handleRemoveTimer('push', index) : undefined}
                        showRemove={editingSchedule.pushReminderTimers.length > 1}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Enable Email Reminders */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableEmailReminders"
                  checked={editingSchedule.enableEmailReminders}
                  onChange={(e) =>
                    handleScheduleChange('enableEmailReminders', e.target.checked)
                  }
                  className="h-4 w-4 text-primary-600 rounded border-gray-300 dark:border-gray-600 focus:ring-primary-500"
                />
                <Tooltip content="Send automated email notifications to participants to remind them about upcoming assessments. Useful as a backup communication method or for participants who prefer email.">
                  <label
                    htmlFor="enableEmailReminders"
                    className="text-sm font-medium text-gray-700 dark:text-gray-200"
                  >
                    Enable Email Reminders
                  </label>
                </Tooltip>
              </div>
              {editingSchedule.enableEmailReminders && (
                <>
                  <MultiSelectDropdown
                    label="Select Email Reminder Template(s)"
                    options={emailReminders.map((er) => ({
                      value: er.id.toString(),
                      label: er.name
                    }))}
                    selectedValues={editingSchedule.emailReminders}
                    onChange={(vals) => handleScheduleChange('emailReminders', vals)}
                    required={editingSchedule.enableEmailReminders}
                    showValidation={showValidation}
                    resolvingLabels={remindersLoading}
                  />
                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <Tooltip content="Set specific times when email reminders will be sent to participants. Multiple times allow for repeated reminders throughout the day. Times are converted to UTC for consistent delivery across time zones.">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Reminder Times</label>
                      </Tooltip>
                      <button
                        type="button"
                        onClick={() => handleAddTimer('email')}
                        className="inline-flex items-center px-2 py-1 text-sm text-primary-600 hover:text-primary-800"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Time
                      </button>
                    </div>
                    {editingSchedule.emailReminderTimers.map((time, index) => (
                      <TimeInput
                        key={index}
                        label={`Time ${index + 1}`}
                        time={time}
                        onChange={(field, value) => handleTimeChange('email', index, field, value)}
                        onRemove={editingSchedule.emailReminderTimers.length > 1 ? () => handleRemoveTimer('email', index) : undefined}
                        showRemove={editingSchedule.emailReminderTimers.length > 1}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Enable Missing Forms */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableMissingForms"
                  checked={editingSchedule.enableMissingForms}
                  onChange={(e) =>
                    handleScheduleChange('enableMissingForms', e.target.checked)
                  }
                  className="h-4 w-4 text-primary-600 rounded border-gray-300 dark:border-gray-600 focus:ring-primary-500"
                />
                <Tooltip content="Automatically send follow-up forms to participants who miss scheduled assessments. This helps ensure data collection completeness by providing a secondary opportunity to complete missed surveys.">
                  <label
                    htmlFor="enableMissingForms"
                    className="text-sm font-medium text-gray-700 dark:text-gray-200"
                  >
                    Missing Forms
                  </label>
                </Tooltip>
              </div>
              {editingSchedule.enableMissingForms && (
                <div className="pl-6">
                  <SingleSelectDropdown
                    label="Select Missing Form"
                    options={missingForms.map((mf) => ({
                      value: mf.id.toString(),
                      label: mf.name
                    }))}
                    selectedValue={editingSchedule.missingForms.length > 0 ? editingSchedule.missingForms[0] : ''}
                    onChange={(val) => handleScheduleChange('missingForms', val ? [val] : [])}
                    required={editingSchedule.enableMissingForms}
                    showValidation={showValidation}
                  />
                </div>
              )}
            </div>

            {/* FECOA-1236: the old standalone "Enable Assessment Window" block
                (checkbox + repeatable named windows with Start/End Time) was removed.
                Its Open/Close Day range now lives in the always-visible consolidated
                Delivery Window above, and intra-day timing moved to timepoints. */}
          </div>
        </div>
      )}
    </motion.div>
  );

  const renderContent = () => {
    switch (step) {
      case 1:
        return renderArmsForm();
      case 2:
        return renderSchedulesForm();
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-4 border-primary-500 border-solid rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading study design...</p>
        </div>
      </div>
    );
  }

  const SurveyOrderingComponent = ({ selectedSurveys, allSurveys, onChange }) => {
    // Find survey names from IDs; null while the list is still loading so the
    // row renders a placeholder instead of flashing the bare id.
    const getSurveyById = (id) => {
      const survey = allSurveys.find(s => s.id.toString() === id.toString());
      if (survey) return survey.name;
      return surveysLoading ? null : `Survey ${id}`;
    };

    // Handle manual reordering with up/down buttons
    const handleMoveUp = (index) => {
      if (index === 0) return; // Already at the top
      const newOrder = [...selectedSurveys];
      [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]]; // Swap
      
      // Update ranks when reordering
      updateSurveyRanks(newOrder);
      
      onChange(newOrder);
    };

    const handleMoveDown = (index) => {
      if (index === selectedSurveys.length - 1) return; // Already at the bottom
      const newOrder = [...selectedSurveys];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]]; // Swap
      
      // Update ranks when reordering
      updateSurveyRanks(newOrder);
      
      onChange(newOrder);
    };
    
    // Helper function to update survey ranks when order changes
    const updateSurveyRanks = (orderedSurveys) => {
      // Immediately update the ranks for all surveys based on their new positions
      orderedSurveys.forEach((surveyId, index) => {
        surveyService.updateSurveyRank(surveyId, index)
          .catch(error => {
            console.error(`Error updating rank for survey ${surveyId}:`, error);
          });
      });
    };

    useEffect(() => {
      // Initialize ranks when surveys are added and component mounts
      // This ensures surveys have ranks even if not manually reordered
      if (selectedSurveys && selectedSurveys.length > 1) {
        updateSurveyRanks(selectedSurveys);
      }
    }, [selectedSurveys.length]); // Only run when the number of surveys changes

    // Don't render if there are fewer than 2 selected surveys
    if (!selectedSurveys || selectedSurveys.length < 2) return null;

    return (
      <div className="mt-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-sm dark:shadow-gray-900/20">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-gray-800 dark:to-gray-800">
          <h3 className="text-md font-medium text-gray-800">Linked Survey Sequence</h3>
          <p className="text-xs text-gray-500 mt-1">
            Use the arrow buttons to change the order. Participants will complete these surveys in sequence.
          </p>
        </div>
        
        <ul className="p-2 space-y-2">
          {selectedSurveys.map((id, index) => (
            <li 
              key={id}
              className="flex items-center justify-between p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
            >
              <div className="flex items-center space-x-3">
                <div className="flex flex-col">
                  <span className="font-medium text-gray-800">
                    {getSurveyById(id) ?? <span className="inline-block w-24 h-3.5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" role="status" aria-label="Loading survey name" />}
                  </span>
                  <span className="text-xs text-gray-500">
                    {index === 0 ? 'First in sequence' : index === selectedSurveys.length - 1 ? 'Last in sequence' : `Step ${index + 1}`}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                {/* Manual up/down buttons */}
                <button
                  type="button"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  className={`p-1 rounded ${
                    index === 0 
                      ? 'text-gray-300 cursor-not-allowed' 
                      : 'text-blue-500 hover:bg-blue-100'
                  }`}
                  title="Move up"
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === selectedSurveys.length - 1}
                  className={`p-1 rounded mr-2 ${
                    index === selectedSurveys.length - 1 
                      ? 'text-gray-300 cursor-not-allowed' 
                      : 'text-blue-500 hover:bg-blue-100'
                  }`}
                  title="Move down"
                >
                  <ArrowDown size={16} />
                </button>
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-primary-100 text-primary-800">
                  {index + 1}
                </span>
              </div>
            </li>
          ))}
        </ul>
        
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center rounded-b-md">
          <div className="text-xs text-gray-500">
            <span className="font-medium">{selectedSurveys.length}</span> surveys in sequence
          </div>
          <div className="text-xs text-blue-600">
            Surveys will be presented to participants in this order
          </div>
        </div>
      </div>
    );
  };

  // Hard block: while the draft is in the approval workflow (in_review/approved)
  // the real edit form is off-limits to everyone. Send them to the read-only
  // review instead. The form JSX below never renders in this state.
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to={`/studybuilder/${encodeURIComponent(studyName)}`}
            className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span>Back to Study Options</span>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-gray-900/20 overflow-visible"
        >
          <div className="px-6 py-4 bg-primary-600 dark:bg-primary-700 flex items-center justify-between gap-4">
            <h2 className="text-xl font-semibold text-white">
              Study Design Configuration
              <span className="font-normal text-white/80"> — {studyName}</span>
            </h2>
            {reviewLocked && (
              <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
                <Lock className="w-3.5 h-3.5" /> Pending Review — Locked
              </span>
            )}
          </div>

          {reviewLocked && (
            <div className="px-6 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/50 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
                <Lock className="w-4 h-4 flex-shrink-0" />
                <span>This design is under review and cannot be edited. You can withdraw the submission to unlock it.</span>
              </div>
              <button
                onClick={handleWithdraw}
                disabled={withdrawing}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-300 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 transition-colors"
              >
                <Undo2 className="w-4 h-4" />
                {withdrawing ? 'Withdrawing…' : 'Withdraw Submission'}
              </button>
            </div>
          )}

          <div className={`px-6 py-8 text-[15px] md:text-base leading-6 ${reviewLocked ? 'pointer-events-none opacity-60 select-none' : ''}`}>
            {/* Stepper / Progress */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex space-x-8">
                  <div className={`flex items-center space-x-2 ${step >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
                    <Users className="w-5 h-5" />
                    <span className="font-medium">Arms</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${step >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
                    <Calendar className="w-5 h-5" />
                    <span className="font-medium">Schedules</span>
                  </div>
                </div>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-600 transition-all duration-300"
                  style={{ width: `${(step / 2) * 100}%` }}
                />
              </div>
            </div>

            {/* Step content */}
            {renderContent()}

            {/* Nav Buttons */}
            <div className="mt-8 flex justify-between items-center gap-4">
              <div>
                {step === 2 && (
                  <button
                    onClick={() => setPreviewModalOpen(true)}
                    disabled={selectedScheduleIndex === null || selectedScheduleIndex === undefined}
                    className={`inline-flex items-center gap-2 px-4 py-2 border rounded-md text-sm font-medium ${
                      selectedScheduleIndex === null || selectedScheduleIndex === undefined
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-primary-500 text-primary-700 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-300 dark:hover:bg-primary-900/20'
                    }`}
                  >
                    <Calendar className="w-4 h-4" />
                    Preview Schedule
                  </button>
                )}
              </div>
              <div className="flex items-center gap-4">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Previous
                </button>
              )}
              {step === 1 ? (
                <button
                  onClick={() => setStep(2)}
                  disabled={arms.some(arm => !arm.trim()) || arms.length === 0}
                  className={`px-4 py-2 border border-transparent rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-white ${
                    arms.some(arm => !arm.trim()) || arms.length === 0
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                  }`}
                >
                  Next
                </button>
              ) : (
                /* Save Design split button (FECOA-1600): primary saves a draft;
                   the caret opens Save as Draft / Save & Submit for Approval. */
                <div className="relative inline-flex">
                  <button
                    onClick={handleSubmit}
                    disabled={!schedules || schedules.length === 0}
                    className={`px-4 py-2 border border-transparent rounded-l-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-white ${
                      !schedules || schedules.length === 0
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                    }`}
                  >
                    Save Design
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSaveMenu((v) => !v)}
                    disabled={!schedules || schedules.length === 0}
                    aria-label="More save options"
                    className={`px-2 border border-l-0 border-transparent rounded-r-md shadow-sm text-white ${
                      !schedules || schedules.length === 0
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                    }`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {showSaveMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowSaveMenu(false)} />
                      <div className="absolute right-0 bottom-full mb-2 w-60 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
                        <button
                          onClick={(e) => { setShowSaveMenu(false); handleSubmit(e); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <Save className="w-4 h-4 text-gray-400" />
                          Save as Draft
                        </button>
                        <button
                          onClick={handleSaveAndSubmit}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors border-t border-gray-100 dark:border-gray-700"
                        >
                          <Send className="w-4 h-4" />
                          Save &amp; Submit for Approval
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <SchedulePreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        schedules={schedules}
        editingSchedule={editingSchedule}
        editingScheduleIndex={selectedScheduleIndex}
        studyStartDay={studyStartDay}
      />

      {isLiveScheduleId(editingSchedule?.id) && (
        <>
          <AmendScheduleModal
            open={showAmendModal}
            onClose={() => setShowAmendModal(false)}
            scheduleId={editingSchedule.id}
            config={{
              type: editingSchedule.type,
              arms: editingSchedule.arms || [],
              studyStartDay,
              siteType: editingSchedule.siteType,
              standardSurveys: editingSchedule.standardSurveys || [],
              stages: editingSchedule.stages || [],
              timepoints: editingSchedule.timepoints || [],
            }}
          />
          <ScheduleVersionHistoryPanel
            open={showVersionHistory}
            onClose={() => setShowVersionHistory(false)}
            scheduleId={editingSchedule.id}
          />
        </>
      )}

      <SubmitForApprovalModal
        open={submitModalOpen}
        onClose={() => setSubmitModalOpen(false)}
        draftId={draftId}
        draftName={studyName}
        denialFeedback={denialFeedback}
        onSubmitted={() => {
          setSubmitModalOpen(false);
          navigate(`/studybuilder/${encodeURIComponent(studyName)}`);
        }}
      />
    </div>
  );
};

export default StudyDesignsPage;